/* ============================================================================
   Rover — AI Part Finder · storefront wiring  (Phase 5)
   Embeds the approved 10_Rover_Design.html as a Shopify theme section, wired to
   the LIVE /v1 service through the Shopify App Proxy (same-origin /apps/rover/*),
   so the box origin/endpoint is never exposed in page source.
   Contract: 20_API_Contract.md — §3 search · §4 vehicle · §5 interpretation ·
   §6 result/verdict/evidence · §9 UI-element→field provenance.
   READ-ONLY: renders index data; the only writes are /v1/capture (our Postgres)
   and the customer's own /cart/add.js. Never invents fitment.
   ============================================================================ */
(function () {
  "use strict";
  var root = document.getElementById("rover-app");
  if (!root) return;

  var CFG = {
    base: (root.getAttribute("data-proxy-base") || "/apps/rover").replace(/\/+$/, ""),
    lang: root.getAttribute("data-lang") || "auto"
  };
  var api = function (p) { return CFG.base + p; };

  /* ----------------------------- i18n (chrome only) -----------------------------
     Product copy (title/description/faq) comes from /v1 as stored (English). LANG
     controls read-back, labels, evidence chrome and microcopy — never product text. */
  var I18N = {
    en: {
      rbtitlewith: "Here’s what fits your <b>{v}</b>", rbtitlenone: "Here’s what we found",
      resultsfor: "results · ranked by fit, then relevance", whyverdict: "Why this verdict?",
      details: "Details", add: "Add to basket", vat: "excl. VAT", evfoot: "Traceable to SAP item data · fitment is never invented",
      desch: "Description", faqh: "FAQ", alth: "Also sold as", yoursearch: "matches your search", crit: "Safety-checked", codechip: "Code",
      fitbtn: "Fitment", faqbtn: "FAQ", detbtn: "Details", fith: "Fits these models", fitall: "Fits all models / general",
      fitnone: "No model fitment listed yet — paste your VIN to confirm", grouphdr: "Same part · {n} options", qualopts: "{n} quality options", qtylab: "Qty",
      crittip: "Safety-critical category — checked to a stricter bar; never a false green.",
      loading: "Searching…", errtitle: "Search didn’t come back",
      errbody: "Something went wrong on our side and the search didn’t come back. Your vehicle is still saved.",
      retry: "Try again", zerotitle: "No exact match — here are the closest options",
      zerobody: "Nothing matched that precisely. The closest items are below. If none are right, ask us to track down the exact part.",
      bestguess: "Closest matches", cap1: "What are you after?", cap2: "Your vehicle", cap3: "Email (so we can reply)",
      track: "Track it down for me", capdone: "On it — we’ll email you within one working day.",
      emptytitle: "Describe the part you need", emptybody: "Type a part, a symptom, a code or your VIN above and we’ll find what fits.",
      addedok: "Added to basket", addfail: "Couldn’t add — opening the product page…",
      vinnudge: "Tip: paste your VIN for an exact fit on safety-critical parts (brakes, steering).",
      precmodel: "Model-level fit — safety-critical variants stay “confirm” until you add a VIN.",
      precvariant: "Variant-level fit — exact fit resolved from your VIN/chassis.",
      ukmoved: "UK plate lookup isn’t available yet — please pick your model manually.",
      confirmgen: "Confirm the generation:", usethis: "Use this vehicle", change: "Change", addveh: "Add your Land Rover",
      homed: "Reset — fresh start.",
      stock_in: "In stock", stock_low: "Low stock", stock_out: "Out of stock",
      fitlabels: { green: "Fits your vehicle", amber: "Confirm variant", grey: "Fit unconfirmed", none: "Not listed for your vehicle" },
      // --- Guided Search (Phase 6, §13) ---
      narrowoptional: "Optional — or just open any part below",  // the question is a refiner, never a gate
      showall: "Show all {n} matches", showmore: "Show more", showmoreing: "Fetching…",
      showallresults: "Show all results", tailhdr: "Other possible matches",
      previewfor: "best matches · narrow to refine, or open any part",
      pasteVin: "Paste your VIN for an exact fit",
      humancta: "Can’t find it? Ask our team — a bit slower, but very reliable.",
      humanctafoot: "Not quite what you wanted? Ask us.",
      humanbtn: "Contact us",
      contactsent: "Thanks — we’ll email you within one working day.",
      glbrowse: "Browsing as", glclear: "Clear vehicle"
    },
    nl: {
      rbtitlewith: "Dit past op je <b>{v}</b>", rbtitlenone: "Dit vonden we",
      resultsfor: "resultaten · gesorteerd op pasvorm, dan relevantie", whyverdict: "Waarom dit oordeel?",
      details: "Details", add: "In winkelwagen", vat: "excl. btw", evfoot: "Herleidbaar tot SAP-artikeldata · pasvorm wordt nooit verzonnen",
      desch: "Omschrijving", faqh: "Veelgestelde vragen", alth: "Ook verkocht als", yoursearch: "komt overeen met je zoekopdracht", crit: "Veiligheid gecheckt", codechip: "Code",
      fitbtn: "Pasvorm", faqbtn: "FAQ", detbtn: "Details", fith: "Past op deze modellen", fitall: "Past op alle modellen / universeel",
      fitnone: "Nog geen modelpasvorm bekend — plak je VIN om te bevestigen", grouphdr: "Zelfde onderdeel · {n} opties", qualopts: "{n} kwaliteitsopties", qtylab: "Aantal",
      crittip: "Veiligheidskritische categorie — strenger gecontroleerd; nooit een vals groen.",
      loading: "Zoeken…", errtitle: "Er ging iets mis",
      errbody: "Er ging iets mis aan onze kant en de zoekopdracht kwam niet terug. Je auto is nog bewaard.",
      retry: "Opnieuw", zerotitle: "Geen exacte match — dit zijn de dichtstbijzijnde opties",
      zerobody: "Niets kwam precies overeen. De dichtstbijzijnde artikelen staan hieronder. Klopt er niets? Vraag ons het exacte onderdeel op te sporen.",
      bestguess: "Dichtstbijzijnde resultaten", cap1: "Wat zoek je?", cap2: "Jouw auto", cap3: "E-mail (voor ons antwoord)",
      track: "Spoor het voor me op", capdone: "Komt goed — we mailen je binnen één werkdag.",
      emptytitle: "Beschrijf het onderdeel dat je zoekt", emptybody: "Typ hierboven een onderdeel, symptoom, code of je VIN en wij halen op wat past.",
      addedok: "Toegevoegd aan winkelwagen", addfail: "Toevoegen mislukt — productpagina openen…",
      vinnudge: "Tip: plak je VIN voor een exacte pasvorm op veiligheidskritische delen (remmen, besturing).",
      precmodel: "Pasvorm op modelniveau — veiligheidskritische varianten blijven “bevestigen” tot je een VIN toevoegt.",
      precvariant: "Pasvorm op variantniveau — exacte pasvorm bepaald uit je VIN/chassis.",
      ukmoved: "Brits kenteken opzoeken kan nog niet — kies je model handmatig.",
      confirmgen: "Bevestig de generatie:", usethis: "Gebruik deze auto", change: "Wijzig", addveh: "Voeg je Land Rover toe",
      homed: "Opnieuw — fris beginnen.",
      stock_in: "Op voorraad", stock_low: "Weinig voorraad", stock_out: "Niet op voorraad",
      fitlabels: { green: "Past op je auto", amber: "Variant bevestigen", grey: "Pasvorm onbekend", none: "Niet vermeld voor je auto" },
      // --- Guided Search (Phase 6, §13) ---
      narrowoptional: "Optioneel — of open hieronder direct een onderdeel",
      showall: "Toon alle {n} resultaten", showmore: "Toon meer", showmoreing: "Ophalen…",
      showallresults: "Toon alle resultaten", tailhdr: "Andere mogelijke resultaten",
      previewfor: "beste matches · verfijn verder, of open een onderdeel",
      pasteVin: "Plak je VIN voor een exacte pasvorm",
      humancta: "Niet gevonden? Vraag het ons team — iets trager, maar oerbetrouwbaar.",
      humanctafoot: "Niet helemaal wat je zocht? Vraag het ons.",
      humanbtn: "Contact",
      contactsent: "Bedankt — we mailen je binnen één werkdag.",
      glbrowse: "Zoeken voor", glclear: "Voertuig wissen"
    }
  };
  function detectLang() {
    var l = (document.documentElement.lang || navigator.language || "en").toLowerCase();
    return l.indexOf("nl") === 0 ? "nl" : "en";
  }
  var LANG = (CFG.lang === "nl" || CFG.lang === "en") ? CFG.lang : detectLang();
  function t() { return I18N[LANG]; }

  /* ----------------------------- state ----------------------------- */
  var VEH = null;                 // normalised vehicle object (contract §4) or null
  var GARAGE = { confirm_models: [], base: null, picked: null }; // working resolve state
  var SID = "rv_" + Math.random().toString(36).slice(2, 12);
  var LASTQ = "";                 // the query currently shown (keeps the refine boxes in sync)
  var CARD_REVEAL = "stagger";    // "stagger" (fresh paint) | "regrade" (in-place vehicle re-grade)

  /* --- Garage-lite session vehicle (§14) ---
     A model narrowing ANSWER auto-adopts as a per-tab pill ("Browsing as: Defender ✕") so the
     NEXT query is pre-scoped without re-asking. Subordinate to the real garage (VEH); per-tab
     (rides the rover_last sessionStorage); QUERY-WINS conflict handling is SERVER-side. */
  var GL_ENABLED = true;          // kill switch — set false to disable adopt + wire + render
  var PILL = null;                // {kind:"model"|"family", key, label} or null
  var GL_FAMILY_MODELS = {        // mirrors vocab.REF_FAMILY — closed, stable vocabulary
    defender: ["def_old", "def_new"],
    discovery: ["disco1", "disco2", "disco3", "disco4", "disco5", "disco_sport"],
    range_rover: ["rrc", "rr_p38", "rr_l322", "rr_l405", "rr_l460"],
    rr_sport: ["rr_sport_l320", "rr_sport_l494", "rr_sport_l461"],
    evoque: ["evoque_1", "evoque_2"], velar: ["velar"],
    freelander: ["freelander1", "freelander2"], series: ["series1", "series_2_3"], jaguar: ["jaguar"]
  };
  var GL_FAMILY_LABELS = { defender: "Defender", discovery: "Discovery", range_rover: "Range Rover",
    rr_sport: "Range Rover Sport", evoque: "Evoque", velar: "Velar", freelander: "Freelander",
    series: "Series", jaguar: "Jaguar" };

  /* "Show more" pager (§13.7). Pages the SAME query confident-first, then the labelled tail.
     results[] = the confident set already in memory (revealed without a round-trip); once exhausted the
     pager fetches deeper pages from /v1/search/more. confidentTotal marks where the "Other possible
     matches" divider drops. Reset on every fresh render. */
  var PAGER = { token: null, results: [], rendered: 0, confidentTotal: 0, serverHasMore: false,
                host: null, busy: false, dividerShown: false };

  /* --- Guided Search (Phase 6, §13) narrowing-round state ---
     FACETS = hard filters accumulated as the customer answers narrowing questions.
     NARROW = {asked:[dimensions], answers:{dimension:value}} echoed to the engine each turn.
     All three reset whenever a NEW (fresh) query is started; they persist across narrowing
     re-calls of the SAME query so the engine sees the cumulative answers and honours the 3-cap. */
  var FACETS = {};
  var NARROW = { asked: [], answers: {} };
  var LASTPREFILL = null;         // capture_prefill from the latest response (seeds the contact modal)
  function resetNarrow() { FACETS = {}; NARROW = { asked: [], answers: {} }; }
  // chip.facet → request facets key (contract §13.3: model→model, position→position, etc.)
  var FACET_KEY = { model: "model", position: "position", variant: "variant", sub_type: "sub_type", quality: "quality" };

  /* ----------------------------- helpers ----------------------------- */
  function $(id) { return document.getElementById(id); }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  // Render trusted SAP/IMDD description HTML (UserText is stored as HTML-as-plaintext).
  // Keep a whitelist of formatting tags — stripped of ALL attributes — and drop everything
  // else (a, img, script, style, iframe, on*-handlers). Belt-and-braces against stray markup.
  var DESC_OK = { b:1, strong:1, i:1, em:1, u:1, br:1, p:1, ul:1, ol:1, li:1, h5:1, h6:1, span:1, small:1 };
  function descHtml(s) {
    if (s == null) return "";
    return String(s).replace(/<\/?\s*([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?>/g, function (m, tag) {
      tag = tag.toLowerCase();
      if (!DESC_OK[tag]) return "";
      return m.charAt(1) === "/" ? "</" + tag + ">" : "<" + tag + ">";
    });
  }
  var fitClass = function (f) { return f === "not_listed" ? "none" : (f || "none"); }; // §8.1
  function vehLabel(v) {
    if (!v) return "";
    return [v.year, (v.model_label || ""), v.engine].filter(Boolean).join(" ").trim() || (v.model_label || "");
  }

  /* ---------- leak guard: no raw internal token ever reaches a customer (B1/B2) ----------
     CATEGORY_LABELS mirrors REF_CATEGORY in rover_engine/vocab.py. The server is the durable
     fix; this is belt-and-braces so a raw token (e.g. "axle_susp") can never render even before
     a service restart. */
  var CATEGORY_LABELS = {
    accessories: "Accessories", axle_susp: "Axle & Suspension", body_chassis: "Body & Chassis",
    braking: "Braking", cables: "Cables", cleaning: "Cleaning", clutch: "Clutch",
    cool_heat: "Cooling & Heating", drive_mech: "Drivetrain", electrical: "Electrical",
    engine: "Engine", exhaust: "Exhaust", fixings_hard: "Fixings & Hardware",
    fuel_system: "Fuel System", gearbox: "Gearbox", gifts: "Gifts", interior: "Interior",
    safety: "Safety", service: "Service Parts", tools: "Tools", wheels: "Wheels & Tyres"
  };
  function titleCaseToken(s) {
    return String(s).replace(/[_\-]+/g, " ").replace(/\s+/g, " ").trim()
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }
  // map an internal token to a human label; leave part codes and real text untouched
  function humanizeVal(v) {
    if (v == null) return "";
    var s = String(v).trim();
    if (CATEGORY_LABELS[s]) return CATEGORY_LABELS[s];
    if (typeof MODEL_LABELS !== "undefined" && MODEL_LABELS[s]) return MODEL_LABELS[s];
    if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(s)) return titleCaseToken(s); // snake_case internal token
    return s;
  }
  function humanizeKey(s) {
    s = String(s == null ? "" : s);
    return (/^[a-z0-9_]+$/.test(s) && s.indexOf("_") >= 0) ? titleCaseToken(s) : s;
  }
  // clean a narrowing-chip label: take the most-specific segment of a tag path, strip dangling
  // separators/trailing commas, humanize tokens, sentence-case (B2).
  function cleanChipLabel(s, facet) {
    if (s == null) return "";
    var t = String(s).replace(/\s+/g, " ").trim();
    // Breadcrumb arrows are always path separators (keep the most-specific segment). Comma/slash are
    // only treated as separators for free-form tag paths (sub_type / category) — NEVER for model /
    // quality / variant / position labels, which legitimately contain "/" (e.g. "Series II / III",
    // "Fits all / general"), so those are no longer truncated to a bare "III". (Fixes the series labels.)
    var pathFacet = (facet !== "model" && facet !== "quality" && facet !== "variant" && facet !== "position");
    var sep = pathFacet ? /[,/›>]/ : /[›>]/;
    if (sep.test(t)) {
      var parts = t.split(sep).map(function (x) { return x.trim(); }).filter(Boolean);
      if (parts.length) t = parts[parts.length - 1];
    }
    t = t.replace(/[\s,;:·•\-]+$/, "").replace(/^[\s,;:·•\-]+/, "").trim();
    t = humanizeVal(t);
    if (t) t = t.charAt(0).toUpperCase() + t.slice(1);
    return t;
  }
  // best-effort part-code extraction from the current query (B4: CODE chip on an empty read-back)
  function parsedCode() {
    var q = (LASTQ || currentQuery() || "").trim();
    var m = q.match(/\b[A-Z]{2,4}\s?\d{3,}[A-Z0-9]*\b|\b\d{6,}[A-Z0-9]*\b/i);
    return m ? m[0].toUpperCase().replace(/\s+/g, "") : "";
  }

  var ICONS = {
    green: '<svg class="ic green" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9"/></svg>',
    amber: '<svg class="ic amber" viewBox="0 0 24 24"><path d="M12 4l8 14H4z"/><path d="M12 10v4"/></svg>',
    grey: '<svg class="ic grey" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.4c-.8.4-1.1.9-1.1 1.7v.3"/><circle cx="12" cy="16.7" r=".7" fill="currentColor" stroke="none"/></svg>',
    none: '<svg class="ic none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M6.2 6.2l11.6 11.6"/></svg>'
  };
  var EV_ICON = '<svg class="ck {lvl}" viewBox="0 0 24 24"><path d="M5 12l4 4L19 6"/></svg>';

  // branded image fallback (B3) — the real Rover paw silhouette on white, never a broken-image box.
  // Exposed on window so an inline onerror (which runs in global scope) can swap it in.
  var PAW_INNER = '<ellipse cx="40" cy="37" rx="8.6" ry="12" transform="rotate(-8 40 37)"/>' +
    '<ellipse cx="60" cy="37" rx="8.6" ry="12" transform="rotate(8 60 37)"/>' +
    '<ellipse cx="22.5" cy="53" rx="7.3" ry="10.4" transform="rotate(-27 22.5 53)"/>' +
    '<ellipse cx="77.5" cy="53" rx="7.3" ry="10.4" transform="rotate(27 77.5 53)"/>' +
    '<path d="M50,49 C62,49 78,60 78,73 C78,83.5 70,89.5 61,89.5 C56,89.5 53,86.8 50,86.8 C47,86.8 44,89.5 39,89.5 C30,89.5 22,83.5 22,73 C22,60 38,49 50,49 Z"/>';
  var PLACEHOLDER_SVG = '';
  try { window.__roverPh = PLACEHOLDER_SVG; } catch (e) {}

  /* ----------------------------- results sort (Best fit / Price) ----------------------------- */
  var CURRENT_SORT = "fit";
  function rvUnitPrice(unit) {
    var ps = unit.querySelectorAll(".price"), min = null;
    for (var i = 0; i < ps.length; i++) {
      var c = ps[i].cloneNode(true); var v = c.querySelector(".vat"); if (v && v.parentNode) v.parentNode.removeChild(v);
      var t = c.textContent.replace(/[^0-9.,]/g, "");
      if (!t) continue;
      if (t.indexOf(",") > -1 && t.indexOf(".") > -1) t = t.replace(/\./g, "").replace(",", ".");
      else if (t.indexOf(",") > -1) t = t.replace(",", ".");
      var n = parseFloat(t); if (!isNaN(n) && (min === null || n < min)) min = n;
    }
    return min;
  }
  function rvApplySort(mode) {
    try {
      var host = $("cards"); if (!host) return;
      var kids = host.children, units = [], tail = [], i;
      for (i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.nodeType === 1 && el.classList && (el.classList.contains("card") || el.classList.contains("fam-group"))) units.push(el);
        else tail.push(el);
      }
      var maxOrd = -1;
      for (i = 0; i < units.length; i++) { var o = parseInt(units[i].getAttribute("data-ord"), 10); if (!isNaN(o) && o > maxOrd) maxOrd = o; }
      for (i = 0; i < units.length; i++) { if (isNaN(parseInt(units[i].getAttribute("data-ord"), 10))) units[i].setAttribute("data-ord", String(++maxOrd)); }
      units.sort(function (a, b) {
        var ao = parseInt(a.getAttribute("data-ord"), 10), bo = parseInt(b.getAttribute("data-ord"), 10);
        if (mode === "price-asc" || mode === "price-desc") {
          var ap = rvUnitPrice(a), bp = rvUnitPrice(b);
          if (ap === null && bp === null) return ao - bo;
          if (ap === null) return 1; if (bp === null) return -1;
          if (ap !== bp) return mode === "price-asc" ? ap - bp : bp - ap;
          return ao - bo;
        }
        return ao - bo;
      });
      for (i = 0; i < units.length; i++) host.appendChild(units[i]);
      for (i = 0; i < tail.length; i++) host.appendChild(tail[i]);
    } catch (e) {}
  }
  // the one recurring paw "tell" (mark A · Pure Paw, currentColor). No fill on the group so the
  // surrounding CSS `.paw{fill:…}` recolours it (green on light, white on green).
  var PAW_SVG = '<svg class="paw" viewBox="0 0 100 100" aria-hidden="true"><g transform="translate(0 -7)">' + PAW_INNER + "</g></svg>";

  /* ----------------------------- /v1 calls ----------------------------- */
  function postJSON(path, body) {
    return fetch(api(path), {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body), credentials: "same-origin"
    }).then(function (r) {
      if (!r.ok) throw new Error(path + " " + r.status);
      return r.json();
    });
  }
  function apiSearch(query) {
    // facets / narrow are additive (§13): an empty FACETS + empty narrow.asked reproduces the
    // pre-Phase-6 request exactly, so the confident happy-path is unchanged.
    return postJSON("/v1/search", {
      query: query, lang: LANG, vehicle: VEH,
      // §14 garage-lite: the per-tab session pill rides as session_vehicle; the server applies it
      // under the QUERY-WINS rule. Never sent when a real vehicle is garaged, or when a model
      // answer already sits in FACETS this round (a this-round answer outranks the pill).
      session_vehicle: (GL_ENABLED && !VEH && PILL && FACETS.model == null)
        ? { kind: PILL.kind, key: PILL.key } : null,
      facets: FACETS,
      narrow: { asked: NARROW.asked.slice(), answers: shallowCopy(NARROW.answers) },
      options: { include_evidence: true, include_faq: true }, session_id: SID
    });
  }
  function apiResolve(payload) { return postJSON("/v1/vehicle/resolve", payload); }
  function apiCapture(payload) { return postJSON("/v1/capture", payload); }
  // §13.4 progressive enrichment — augment-only second pass; failure is non-fatal
  function apiEnrich(token, qid) { return postJSON("/v1/search/enrich", { enrich_token: token, query_id: qid }); }
  // §13.7 "Show more" pager — pages the confident set, then the labelled tail (read-only, augment-only)
  function apiMore(token, offset) { return postJSON("/v1/search/more", { more_token: token, offset: offset }); }
  function shallowCopy(o) { var n = {}; for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) n[k] = o[k]; return n; }

  /* ----------------------------- screen state ----------------------------- */
  function showState(s) {
    var scr = root.querySelectorAll(".screen");
    for (var i = 0; i < scr.length; i++) scr[i].classList.remove("active");
    var el = $("screen-" + s);
    if (el) el.classList.add("active");
  }

  /* ----------------------------- thought-process animation (§13.4) -----------------------------
     Honest staged "thinking": the stages map to the REAL pipeline (parse → search → fitment → grade)
     and the labels are QUERY-AWARE — the parse note quotes what the customer typed; the fitment note
     names their garaged vehicle. Both are pulled from data already on the page (the raw query + VEH),
     so there is NO extra latency and NO claimed "parsed intent" (the parse doesn't exist yet at wait
     time). The stages pace on a fixed schedule but the FINAL stage keeps WORKING until the network
     actually returns — never a fixed-timer "done", never fake theatre on a fast response. A small
     floor stops a sub-floor return from flashing; we never slow a real result beyond it.
     prefers-reduced-motion → static + one honest spoken status. The real per-stage status is
     announced to screen readers via #rover-status (a persistent live region outside the screens). */
  var THINK_STAGES = ["parse", "search", "fitment", "grade"];
  // when each stage becomes ACTIVE. Paced for today's ~3-4s search: the first three notes are spread
  // out (≈0.8-0.9s each) and the gaps WIDEN toward the end so it decelerates into the final "grading"
  // note, which then HOLDS (spins) until the network actually returns. (Revisit if search latency drops
  // well below ~2.5s — at that point earlier steps read better.)
  var THINK_STEPS = [0, 800, 1700, 2600];
  var THINK_FLOOR_MS = 240;                // floor so a sub-floor return doesn't flash (tuned, not padding)
  var THINK_CATALOGUE = 25000;             // shown in the "search" note ("+"-suffixed; index is ~25k+ active)
  function reduceMotion() {
    try { return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch (e) { return false; }
  }
  function announceStatus(txt) { var el = $("rover-status"); if (el) el.textContent = txt; }
  function clipQ(s) { s = (s == null ? "" : String(s)).trim(); var max = (window.innerWidth <= 420) ? 22 : 32; return s.length > max ? s.slice(0, max - 1).trim() + "…" : s; }
  function pickOne(a) { return a[Math.floor(Math.random() * a.length)]; }
  function setStageLabel(box, stage, txt) { var el = box.querySelector('.stage[data-stage="' + stage + '"] .lbl'); if (el) el.textContent = txt; }

  // honest, query-aware stage copy with a small variant pool per phase to avoid sameness. Uses ONLY
  // the raw query + the garaged vehicle; never the parse (which doesn't exist yet) — so always truthful.
  function thinkCopy(query) {
    var q = clipQ(query);
    var veh = vehLabel(VEH);
    var cnt = THINK_CATALOGUE.toLocaleString(LANG === "nl" ? "nl" : "en") + "+";
    var P = (LANG === "nl") ? {
      parse: q ? "Begrijpen wat je bedoelt met ‘" + q + "’" : "Je vraag begrijpen",
      search: [cnt + " onderdelen doorzoeken", "De hele catalogus doorkammen"],
      fitment: veh ? "Controleren of dit op je " + veh + " past" : "Kijken op welke modellen dit past",
      grade: ["Matches op pasvorm beoordelen", "Sorteren op beste pasvorm"]
    } : {
      parse: q ? "Reading what you mean by ‘" + q + "’" : "Reading your request",
      search: ["Searching " + cnt + " parts", "Combing the full catalogue"],
      fitment: veh ? "Checking fitment against your " + veh : "Checking which models this fits",
      grade: ["Grading the matches by fit", "Sorting by what fits best"]
    };
    var g = P.grade, g0 = pickOne(g), g1 = (g.length > 1) ? (g[0] === g0 ? g[1] : g[0]) : g0;
    return { parse: P.parse, search: pickOne(P.search), fitment: P.fitment, grade: g0, gradeAlt: g1 };
  }

  function startThink(query) {
    var box = $("rover-think");
    var copy = thinkCopy(query);
    var t0 = (window.performance && performance.now) ? performance.now() : Date.now();
    var timers = [];
    if (box) {
      var lis = box.querySelectorAll(".stage");
      for (var i = 0; i < lis.length; i++) { lis[i].classList.remove("active", "done", "rin"); }
      setStageLabel(box, "parse", copy.parse); setStageLabel(box, "search", copy.search);
      setStageLabel(box, "fitment", copy.fitment); setStageLabel(box, "grade", copy.grade);
      if (reduceMotion()) {
        // static legible state + ONE honest composed status for screen readers (no motion to narrate)
        for (var s = 0; s < lis.length; s++) lis[s].classList.add("active");
        announceStatus(copy.parse + " · " + copy.fitment);
      } else {
        THINK_STAGES.forEach(function (name, idx) {
          timers.push(setTimeout(function () {
            var cur = box.querySelector('.stage[data-stage="' + name + '"]');
            if (cur) { cur.classList.add("active", "rin"); }
            if (idx > 0) { var prev = box.querySelector('.stage[data-stage="' + THINK_STAGES[idx - 1] + '"]'); if (prev) { prev.classList.remove("active"); prev.classList.add("done"); } }
            announceStatus(copy[name]);   // SR hears the REAL pipeline step, not just motion
            // slow-path: if grade is still working after a beat, refresh its wording so the hold shows life
            if (name === "grade" && copy.gradeAlt && copy.gradeAlt !== copy.grade) {
              timers.push(setTimeout(function () {
                var lbl = box.querySelector('.stage[data-stage="grade"] .lbl');
                if (lbl) { lbl.classList.remove("rin"); void lbl.offsetWidth; lbl.classList.add("rin"); lbl.textContent = copy.gradeAlt; announceStatus(copy.gradeAlt); }
              }, 1200));
            }
          }, THINK_STEPS[idx]));
        });
      }
    }
    return {
      // resolve once BOTH the network has returned AND the minimum display has elapsed
      done: function (cb) {
        var elapsed = ((window.performance && performance.now) ? performance.now() : Date.now()) - t0;
        var wait = Math.max(0, THINK_FLOOR_MS - elapsed);
        setTimeout(function () {
          timers.forEach(clearTimeout);
          // settle every stage to done before the hand-off so a fast return never cuts mid-spin
          if (box) { var done = box.querySelectorAll(".stage"); for (var i = 0; i < done.length; i++) { done[i].classList.remove("active"); done[i].classList.add("done"); } }
          cb();
        }, wait);
      },
      cancel: function () { timers.forEach(clearTimeout); }
    };
  }

  /* ----------------------------- instant quick-hits (tier-1 echo during the wait) -----------------
     The quick-search blob is usually already in this browser (the customer just typed in an enhanced
     box). The moment a full search starts we paint its top literal matches inside the loading screen,
     labelled honestly as UNGRADED — obvious code/title hits feel instant, and the graded results
     replace the whole screen exactly as before. Display-only: rows deep-link to product pages;
     nothing here feeds ranking, fitment or the verdicts. Skipped silently when the blob isn't
     loaded yet or nothing literal matches. */
  var QH_STYLED = false;
  function quickHitsHost() {
    var wrap = root.querySelector("#screen-loading .loadwrap");
    if (!wrap) return null;
    var host = $("rover-quickhits");
    if (!host) {
      host = document.createElement("div");
      host.id = "rover-quickhits";
      wrap.insertBefore(host, wrap.querySelector(".skel") || null);
    }
    if (!QH_STYLED) {
      QH_STYLED = true;
      var st = document.createElement("style");
      st.textContent =
        "#rover-quickhits{margin:16px auto 2px;max-width:680px;text-align:left}" +
        "#rover-quickhits .qh-lab{font-size:12.5px;font-weight:700;letter-spacing:.02em;color:#4a5548;margin:0 0 8px;text-transform:uppercase}" +
        "#rover-quickhits .qh-row{display:flex;align-items:center;gap:12px;padding:9px 12px;background:#fff;border:1px solid #e3e7e1;border-radius:10px;margin-bottom:8px;text-decoration:none;color:inherit;transition:border-color .15s}" +
        "#rover-quickhits .qh-row:hover{border-color:#25581A}" +
        "#rover-quickhits .qh-thumb{width:40px;height:40px;object-fit:contain;border-radius:6px;background:#f4f5f3;flex:none}" +
        "#rover-quickhits .qh-body{flex:1;min-width:0}" +
        "#rover-quickhits .qh-t{display:block;font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}" +
        "#rover-quickhits .qh-sub{display:flex;gap:10px;font-size:12.5px;color:#5b6558;margin-top:2px}" +
        "#rover-quickhits .qh-code{font-family:ui-monospace,Menlo,Consolas,monospace}" +
        "#rover-quickhits .qh-sub{align-items:center}" +
        "#rover-quickhits .qh-qual{font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;border:1px solid #cfd6cb;border-radius:4px;padding:1px 6px;color:#4a5548;white-space:nowrap}" +
        "#rover-quickhits .qh-in_stock{color:#2f7a2f}#rover-quickhits .qh-low_stock{color:#9a6a12}#rover-quickhits .qh-out_of_stock{color:#b3261e}" +
        "#rover-quickhits .qh-price{font-weight:700;font-size:13.5px;white-space:nowrap}";
      document.head.appendChild(st);
    }
    return host;
  }
  function renderQuickHits(query, attempt) {
    var host = quickHitsHost();
    if (!host) return;
    if (!attempt) host.innerHTML = "";
    var qs = window.RoverQS;
    var hits = null;
    if (qs && typeof qs.match === "function") {
      try { hits = qs.match(query, 4); } catch (e) { hits = null; }
    }
    if (hits === null) {
      // Two cold-start cases land here, both COMMON on the /?rover_q= hand-off (fresh page, search
      // starts immediately): (a) rover-quicksearch.js (deferred, loads after this file) hasn't
      // executed yet, so the match hook doesn't exist; (b) the hook exists but the blob is still
      // loading. Retry briefly while the loading screen is still up and the query hasn't been
      // superseded, so escalated searches get their instant strip a beat late instead of never.
      // Gives up silently after ~2.7s (also covers quick-search being kill-switched off).
      if ((attempt || 0) < 6) setTimeout(function () {
        var scr = $("screen-loading");
        if (scr && scr.classList.contains("active") && LASTQ === query) renderQuickHits(query, (attempt || 0) + 1);
      }, 450);
      return;
    }
    if (!hits.length) return;
    hits = hits.filter(function (p) { return p && p.url; });
    if (!hits.length) return;
    var lab = (LANG === "nl") ? "Directe treffers — Rover controleert intussen de pasvorm…"
                              : "Instant matches — Rover is still checking fitment…";
    var html = '<div class="qh-lab">' + esc(lab) + "</div>";
    hits.forEach(function (p) {
      var stockTxt = (p.stock_state && typeof qs.stockText === "function") ? qs.stockText(p.stock_qty, p.stock_state) : "";
      var stock = stockTxt ? '<span class="qh-stock qh-' + esc(p.stock_state) + '">' + esc(stockTxt) + "</span>" : "";
      var qual = p.quality ? '<span class="qh-qual">' + esc(p.quality) + "</span>" : "";
      html += '<a class="qh-row" data-id="' + esc(p.id || "") + '" href="' + esc(p.url) + '">' +
        (p.thumb ? '<img class="qh-thumb" src="' + esc(p.thumb) + '" alt="" loading="lazy">' : '<span class="qh-thumb" aria-hidden="true"></span>') +
        '<span class="qh-body"><span class="qh-t">' + esc(p.title || p.code) + "</span>" +
        '<span class="qh-sub"><span class="qh-code">' + esc(p.code || "") + "</span>" + qual + stock + "</span></span>" +
        (p.price_display ? '<span class="qh-price">' + esc(p.price_display) + "</span>" : "") + "</a>";
    });
    host.innerHTML = html;
    // Live-stock patch — same /v1/stock batch + cache as the dropdown and the result cards. The strip
    // paints from the browser-cached blob, which can lag a recent stock movement by many minutes
    // (Brad's ANR2157 case: strip said 9, truth was 0). Display-only, and it pre-warms the client
    // cache so the result cards that follow patch instantly for overlapping items.
    if (typeof qs.liveStock === "function") {
      var ids = [];
      hits.forEach(function (p) { if (p.id) ids.push(p.id); });
      if (ids.length) {
        var rowEls = host.querySelectorAll(".qh-row");
        qs.liveStock(ids, function (id, s) {
          if (!s || !s.state) return;
          var txt = (typeof qs.stockText === "function") ? qs.stockText(s.qty, s.state) : "";
          if (!txt) return;
          for (var i = 0; i < rowEls.length; i++) {
            if (rowEls[i].getAttribute("data-id") !== id) continue;
            var span = rowEls[i].querySelector(".qh-stock");
            if (!span) {
              var sub = rowEls[i].querySelector(".qh-sub");
              if (!sub) continue;
              span = document.createElement("span");
              sub.appendChild(span);
            }
            span.className = "qh-stock qh-" + s.state;
            span.textContent = txt;
          }
        });
      }
    }
  }

  /* ----------------------------- search round-trip ----------------------------- */
  var lastQID = null;
  var lastEnrichToken = null;
  // runSearch(query [, isNarrowing]) — a fresh search resets the narrowing round; a narrowing
  // re-call (chip tap / skip) keeps the accumulated FACETS + NARROW so the engine sees them all.
  function runSearch(query, isNarrowing) {
    if (!query || !query.trim()) return;
    if (!isNarrowing) resetNarrow();
    LASTQ = query;
    ["q-results", "q-zero"].forEach(function (id) { var e = $(id); if (e) e.value = query; });
    showState("loading");
    var loadEl = $("rover-loading-msg"); if (loadEl) loadEl.textContent = t().loading;
    var anim = startThink(query);
    renderQuickHits(query);   // instant tier-1 echo while the graded search runs (no-op if blob absent)
    apiSearch(query).then(function (data) {
      anim.done(function () { renderResults(data); });   // hold for the min-display floor, then paint
    }).catch(function () {
      anim.cancel(); showState("error"); paintError();
    });
  }

  function renderResults(data, quiet) {
    // quiet === true: an in-place re-grade (vehicle applied to a list already on screen) — cards settle
    // with a gentle re-grade beat instead of a fresh staggered paint, and we never scroll-jump (C3).
    CARD_REVEAL = quiet ? "regrade" : "stagger";
    if (data.lang === "en" || data.lang === "nl") LANG = data.lang; // resolved language wins
    lastQID = data.query_id || null;
    lastEnrichToken = data.enrich_token || null;
    LASTPREFILL = data.capture_prefill || null;
    adoptPillFromResponse(data);   // §14: a model ANSWER becomes the session pill (post-response)
    saveLast();   // remember this search so browser-back from a product page restores the results

    // read-back (interpretation.readback_title is optional in the service; build from vehicle if absent)
    var glApplied = GL_ENABLED && !VEH && PILL && data.interpretation &&
      data.interpretation.session_scope === "applied";
    var rbTitle = (data.interpretation && data.interpretation.readback_title) ||
      (VEH ? t().rbtitlewith.replace("{v}", esc(vehLabel(VEH)))
           : (glApplied ? t().rbtitlewith.replace("{v}", esc(PILL.label)) : t().rbtitlenone));
    var rb = $("rb-title"); if (rb) { rb.innerHTML = rbTitle; if (!quiet) { try { rb.focus(); } catch (e) {} } }
    announceStatus("");   // clear the wait status; the readback (role="status") now announces the result
    renderChips((data.interpretation && data.interpretation.chips) || []);

    var meta = data.meta || {};
    var cnt = $("res-count"); if (cnt) cnt.textContent = (meta.total != null ? meta.total : (data.results || []).length);
    var cfor = $("res-count-for"); if (cfor) cfor.textContent = t().resultsfor;

    // ----- Guided Search state machine (§13.1). state is additive: absent ⇒ legacy path. -----
    var state = data.state || (data.zero_result ? "no_confident_results" : "results");

    if (state === "no_confident_results") { renderZero(data); showState("zero"); return; }
    // legacy guards preserved: explicit zero_result, or a results state with nothing to show
    if (data.zero_result) { renderZero(data); showState("zero"); return; }

    if (state === "needs_clarification" && data.question) {
      renderClarification(data);
      showState("results");
      if (!quiet) window.scrollTo(0, root.offsetTop - 10);
      return;
    }

    // state === "results" (the unchanged happy path) ---------------------------------------
    if (!data.results || !data.results.length) { renderEmpty(); showState("zero"); return; }
    hideNarrowPanel();
    renderCards(data.results, $("cards"));
    setResCount(countCards($("cards")));                       // #1: header counts what's actually shown
    setupPager(data, $("cards"), (data.results || []).length); // #6: growing "Show more" + tail
    renderHumanCta("human-cta-results");
    showState("results");
    if (!quiet) window.scrollTo(0, root.offsetTop - 10);
    // fire the augment-only enrichment pass in the background once the shortlist is shown (§13.4)
    if (lastEnrichToken) {
      apiEnrich(lastEnrichToken, lastQID)
        .then(function (enr) { applyEnrichment(enr); })
        .catch(function () { /* first response stands alone */ });
    }
  }

  /* ----------------------------- Feature 1: adaptive narrowing panel (§13.3) ----------------------------- */
  function hideNarrowPanel() {
    var p = $("narrow-panel"); if (p) { p.hidden = true; p.innerHTML = ""; }
  }
  function renderClarification(data) {
    // The narrowing question is an OPTIONAL refiner that sits ABOVE the results, never a gate; results
    // are live and clickable the whole time. We only show a question when it materially narrows the set.
    var q = data.question || {};
    var isPicker = q.kind === "model_picker";   // §13 model picker: complete family→generation, free text
    var all = data.results || [];
    var ch = $("cards");
    if (ch) ch.classList.remove("ctx-faded");

    var prompt = (q.prompt && (q.prompt[LANG] || q.prompt.en || q.prompt.nl)) || "";

    // B2/C4 — clean, dedupe, humanize the chip labels; drop count-1 noise; merge collapses; only keep a
    // "narrow" that materially cuts the set. (Server-side narrow.py is the durable fix; this is the guard.)
    var cleanChips = [], seen = {};
    (q.chips || []).forEach(function (c) {
      var label = cleanChipLabel(c.label != null ? c.label : c.value, c.facet || q.dimension);
      if (!label) return;
      var cnt = (c.candidate_count != null) ? c.candidate_count : null;
      if (cnt != null && cnt <= 1 && !isPicker) return;    // count-1 = noise — EXCEPT the picker, which must stay COMPLETE
      var k = label.toLowerCase();
      if (seen[k] != null) { var ex = cleanChips[seen[k]]; if (cnt != null) ex.candidate_count = (ex.candidate_count || 0) + cnt; return; }
      seen[k] = cleanChips.length;
      cleanChips.push({ label: label, value: c.value, facet: c.facet || q.dimension || "", candidate_count: cnt,
        generations: (c.generations && c.generations.length > 1) ? c.generations : null });
    });
    var hasQuestion = !!prompt && (cleanChips.length >= 2 ||
      (isPicker && cleanChips.length === 1 && !!cleanChips[0].generations));

    // cards: a confident preview when a useful question is offered; otherwise the full list. Either way the
    // growing "Show more" pager (§13.7) is attached so the set can page on into the labelled tail.
    if (ch) {
      if (hasQuestion) {
        var CAP = 6;
        renderCards(all.slice(0, CAP), ch);
        setResCount(countCards(ch));
        setupPager(data, ch, Math.min(CAP, all.length));
        var cfor1 = $("res-count-for"); if (cfor1) cfor1.textContent = t().previewfor;
      } else {
        renderCards(all, ch);
        setResCount(countCards(ch));
        setupPager(data, ch, all.length);
        var cfor2 = $("res-count-for"); if (cfor2) cfor2.textContent = t().resultsfor;
      }
    }
    renderHumanCta("human-cta-results");

    // not a material narrow → no question; the results stand on their own
    if (!hasQuestion) { hideNarrowPanel(); return; }

    // C2 — once a VIN/variant precision is applied we already HAVE the VIN, so drop the "paste your VIN" nudge
    var nudge = q.safety_nudge ? (q.safety_nudge[LANG] || q.safety_nudge.en || q.safety_nudge.nl) : "";
    if (VEH && VEH.precision === "variant") nudge = "";

    // chips are equal options — no recommended/default styling. A family chip with several
    // generations expands to a generation row (two-step picker) instead of answering directly.
    function chipBtn(c, i) {
      var cc = (c.candidate_count != null) ? '<span class="nc">' + esc(String(c.candidate_count)) + "</span>" : "";
      var caret = c.generations ? ' <span class="nc" aria-hidden="true">&rsaquo;</span>' : "";
      return '<button type="button" class="narrow-chip" data-idx="' + i + '" data-facet="' + esc(c.facet) +
        '" data-value="' + esc(c.value) + '"><span class="cl">' + esc(c.label) + "</span>" + cc + caret + "</button>";
    }
    var chipsHTML = cleanChips.map(function (c, i) { return chipBtn(c, i); }).join("");

    var safetyBlock = "";
    if (nudge) {
      safetyBlock = '<div class="narrow-safety"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v6c0 4-3 6.5-7 9-4-2.5-7-5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>' +
        '<span>' + esc(nudge) + '</span>' +
        '<button type="button" class="narrow-vin" data-act="garage">' + esc(t().pasteVin) + "</button></div>";
    }

    var skipLabel = (q.skip_label && (q.skip_label[LANG] || q.skip_label.en)) || t().showallresults;
    var freeTextHTML = "";
    if (isPicker && q.allow_free_text) {
      var ftHint = (q.free_text_hint && (q.free_text_hint[LANG] || q.free_text_hint.en)) ||
        (LANG === "nl" ? "Mijn auto staat er niet bij — typ hem" : "My vehicle isn't listed — type it");
      freeTextHTML =
        '<div class="narrow-freetext">' +
        '<button type="button" class="narrow-showall" data-act="ft-open">' + esc(ftHint) + "</button>" +
        '<form data-act="ft-form" hidden style="display:none;gap:8px;margin-top:8px">' +
        '<input type="text" maxlength="80" autocomplete="off" placeholder="' +
        esc(LANG === "nl" ? "bijv. Defender 2004 of VIN" : "e.g. Defender 2004 or VIN") + '"' +
        ' style="flex:1;min-width:0;padding:8px 12px;border:1px solid #cfd7cd;border-radius:8px;font:inherit">' +
        '<button type="submit" class="narrow-chip"><span class="cl">' +
        esc(LANG === "nl" ? "Zoek" : "Go") + "</span></button></form></div>";
    }

    var html =
      '<div class="narrow-head"><span class="rb-ico" aria-hidden="true"><svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"#25581A\" stroke-width=\"2\" stroke-linejoin=\"round\" stroke-linecap=\"round\"><path d=\"M3 5h18l-7 8v6l-4 2v-8z\"/></svg></span>' +
      '<div class="narrow-headtext"><p class="narrow-prompt">' + esc(prompt) + "</p>" +
      '<p class="narrow-optional">' + esc(t().narrowoptional) + "</p></div></div>" +
      safetyBlock +
      '<div class="narrow-chips" role="group" aria-label="' + esc(prompt) + '">' + chipsHTML + "</div>" +
      freeTextHTML +
      '<button type="button" class="narrow-showall" data-act="showall">' + esc(skipLabel) + "</button>";

    var p = $("narrow-panel");
    if (!p) return;
    p.innerHTML = html;
    p.hidden = false;
    p.setAttribute("data-dimension", q.dimension || "");

    // chip tap: family-with-generations expands to the generation row; anything else answers
    // (adds the value to FACETS, records the answer, re-calls /v1/search as a narrowing turn)
    function tapChip(btn) {
      var idx = parseInt(btn.getAttribute("data-idx") || "-1", 10);
      var c = idx >= 0 ? cleanChips[idx] : null;
      if (isPicker && c && c.generations) { renderGenRow(c); return; }
      answerQuestion(btn, q.dimension);
    }
    function renderGenRow(fam) {
      var host = p.querySelector(".narrow-chips"); if (!host) return;
      var back = '<button type="button" class="narrow-chip" data-act="back"><span class="cl">&lsaquo; ' +
        esc(LANG === "nl" ? "Alle modellen" : "All models") + "</span></button>";
      var gens = fam.generations.map(function (g) {
        var cc = (g.candidate_count != null) ? '<span class="nc">' + esc(String(g.candidate_count)) + "</span>" : "";
        return '<button type="button" class="narrow-chip" data-facet="' + esc(g.facet || "model") +
          '" data-value="' + esc(g.value) + '"><span class="cl">' +
          esc(cleanChipLabel(g.label || g.value, "model")) + "</span>" + cc + "</button>";
      }).join("");
      var allFam = '<button type="button" class="narrow-chip" data-facet="' + esc(fam.facet) +
        '" data-value="' + esc(fam.value) + '"><span class="cl">' +
        esc((LANG === "nl" ? "Alle " : "All ") + fam.label) + "</span></button>";
      host.innerHTML = back + gens + allFam;
      wireChipRow(host);
      var f = host.querySelector(".narrow-chip"); if (f) f.focus();
    }
    function wireChipRow(host) {
      var btns = host.querySelectorAll(".narrow-chip");
      for (var i = 0; i < btns.length; i++) {
        btns[i].addEventListener("click", function (e) {
          var b = e.currentTarget;
          if (b.getAttribute("data-act") === "back") { host.innerHTML = chipsHTML; wireChipRow(host); return; }
          tapChip(b);
        });
        btns[i].addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { e.preventDefault(); e.currentTarget.click(); }
        });
      }
    }
    wireChipRow(p.querySelector(".narrow-chips"));
    var sa = p.querySelector('[data-act="showall"]');
    if (sa) sa.addEventListener("click", function () { showAllResults(data, all); });
    var fto = p.querySelector('[data-act="ft-open"]');
    var ftf = p.querySelector('[data-act="ft-form"]');
    if (fto && ftf) {
      fto.addEventListener("click", function () {
        fto.hidden = true; ftf.hidden = false; ftf.style.display = "flex";
        var inp = ftf.querySelector("input"); if (inp) inp.focus();
      });
      ftf.addEventListener("submit", function (e) {
        e.preventDefault();
        var inp = ftf.querySelector("input");
        answerModelText(q.dimension, inp ? inp.value : "");
      });
    }
    wireActs();
  }

  function answerQuestion(btn, dimension) {
    var facet = btn.getAttribute("data-facet");
    var value = btn.getAttribute("data-value");
    var key = FACET_KEY[facet] || facet;
    if (key) FACETS[key] = value;                   // hard filter (§13.3)
    if (dimension && NARROW.asked.indexOf(dimension) === -1) NARROW.asked.push(dimension);
    if (dimension) NARROW.answers[dimension] = value;
    var ch = $("cards"); if (ch) ch.classList.remove("ctx-faded");
    runSearch(LASTQ, true);                          // narrowing re-call — keep the round state
  }

  // free-typed vehicle answer (§13 model picker): a 17-char VIN becomes the garaged vehicle (persists,
  // exact-fit verdicts); anything else is submitted as facets.model — the SERVICE resolves it (family /
  // model / year) and logs unresolved text as a vocab gap. Never guesses client-side.
  function answerModelText(dimension, text) {
    text = String(text || "").trim();
    if (!text) return;
    var vin = text.replace(/[^A-Za-z0-9]/g, "");
    if (vin.length === 17) {
      apiResolve({ method: "vin", vin: vin }).then(function (res) {
        if (res && res.vehicle && res.vehicle.model_key) {
          VEH = res.vehicle;
          if (VEH.chassis_sequence || VEH.vin) VEH.precision = "variant";
          if (dimension && NARROW.asked.indexOf(dimension) === -1) NARROW.asked.push(dimension);
          renderVeh();
          runSearch(LASTQ, true);
        } else { submitModelText(dimension, text); }
      }).catch(function () { submitModelText(dimension, text); });
      return;
    }
    submitModelText(dimension, text);
  }
  function submitModelText(dimension, text) {
    FACETS.model = text;
    if (dimension && NARROW.asked.indexOf(dimension) === -1) NARROW.asked.push(dimension);
    if (dimension) NARROW.answers[dimension] = text;
    runSearch(LASTQ, true);
  }

  /* ----------------------------- Feature 2: progressive enrichment apply (§13.4) -----------------------------
     AUGMENT-ONLY: patches[] fill/refresh detail on already-shown cards (joined by id); added[]
     appends cautiously-widened extra cards. We NEVER touch a shown card's fit/fit_label/rail
     (no downgrade, no false green) — only the expandable detail (fitment notes, alternative
     codes, faq, description) is patched in place. */
  function applyEnrichment(enr) {
    if (!enr) return;
    (enr.patches || []).forEach(function (patch) {
      if (!patch || !patch.id) return;
      patchCard(patch);
    });
    var added = enr.added || [];
    if (added.length) {
      var host = $("cards"); if (!host) return;
      // render the widened extras into a temp host then append (never re-render the shown cards,
      // so their verdicts are untouched). Use a sentinel wrapper so a second enrich is idempotent.
      var moreBtn = host.querySelector(".rover-more");
      added.forEach(function (p) {
        if (!p || !p.id) return;
        if (host.querySelector('#card-' + cssEsc(p.id))) return;   // already present — don't duplicate
        var tmp = document.createElement("div");
        tmp.innerHTML = cardHTML(p, false);
        var node = tmp.firstChild;
        if (node) {
          node.classList.add("rover-added");
          if (moreBtn) host.insertBefore(node, moreBtn); else host.appendChild(node);
          wireCardControls(node);
        }
      });
      setResCount(countCards(host));   // #1: keep the header in step with the widened list
    }
  }
  function cssEsc(s) { return String(s).replace(/[^a-zA-Z0-9_-]/g, "\\$&"); }

  // Patch a SINGLE already-shown card's expandable detail in place. Fit verdict is left alone.
  function patchCard(patch) {
    var card = document.getElementById("card-" + patch.id);
    if (!card) return;
    // fitment: refresh the fitment expander body (notes / models / general) without touching the rail
    if (patch.fitment) {
      var fit = patch.fitment || {};
      var sec = card.querySelector("#sec-fit-" + idAttr(patch.id));
      if (sec) {
        var fmodels = (fit.models || []).map(function (m) { return '<span class="fmodel">' + esc(m.label) + "</span>"; }).join("");
        var inner = "<h5>" + esc(t().fith) + "</h5>";
        if (fit.general) inner += '<div class="fit-all">' + esc(t().fitall) + "</div>";
        if (fmodels) inner += '<div class="fmodels">' + fmodels + "</div>";
        if (fit.notes) inner += '<div class="fnotes">' + esc(fit.notes) + "</div>";
        if (!fmodels && !fit.general && !fit.notes) inner += '<div class="fnone">' + esc(t().fitnone) + "</div>";
        var wasOpen = sec.classList.contains("open");
        sec.innerHTML = inner;
        if (wasOpen) sec.classList.add("open");
      }
    }
    // alternative_codes + description: rebuild / create the Details section
    if (patch.alternative_codes || patch.description) {
      ensureDetails(card, patch);
    }
    // faq: rebuild / create the FAQ section
    if (patch.faq && patch.faq.length) {
      ensureFaq(card, patch);
    }
    card.classList.add("rover-enriched");
  }
  function idAttr(id) { return String(id); }

  function ensureDetails(card, patch) {
    var pid = patch.id;
    var sec = card.querySelector("#sec-det-" + idAttr(pid));
    var alts = (patch.alternative_codes || []).map(function (a) { return '<span class="ac">' + esc(a) + "</span>"; }).join("");
    var inner = (patch.description ? "<h5>" + esc(t().desch) + '</h5><div class="rdesc">' + descHtml(patch.description) + "</div>" : "") +
      (alts ? "<h5>" + esc(t().alth) + '</h5><div class="altcodes">' + alts + "</div>" : "");
    if (!inner) return;
    if (sec) { var wasOpen = sec.classList.contains("open"); sec.innerHTML = inner; if (wasOpen) sec.classList.add("open"); return; }
    // create the section + its toggle if the first response didn't have details
    addExpander(card, "det-" + pid, t().detbtn, inner, false);
  }
  function ensureFaq(card, patch) {
    var pid = patch.id;
    var sec = card.querySelector("#sec-faq-" + idAttr(pid));
    var faq = (patch.faq || []).map(function (qx) { return "<dt>" + esc(qx.q) + "</dt><dd>" + esc(qx.a) + "</dd>"; }).join("");
    if (!faq) return;
    var inner = "<h5>" + esc(t().faqh) + '</h5><dl class="faq">' + faq + "</dl>";
    if (sec) { var wasOpen = sec.classList.contains("open"); sec.innerHTML = inner; if (wasOpen) sec.classList.add("open"); return; }
    addExpander(card, "faq-" + pid, t().faqbtn, inner, false);
  }
  // add a new expander (toggle + section) to a shown card during enrichment
  function addExpander(card, sec, label, innerHTML, isEvidence) {
    var row = card.querySelector(".exp-row");
    var holder = card.querySelector(".expanders");
    if (!row || !holder) return;
    if (card.querySelector('.linkbtn.ex[data-sec="' + sec + '"]')) return; // toggle already exists
    var btn = document.createElement("button");
    btn.className = "linkbtn ex"; btn.setAttribute("aria-expanded", "false"); btn.setAttribute("data-sec", sec);
    btn.innerHTML = CHEV + esc(label);
    btn.addEventListener("click", function () { toggleSec(sec); });
    row.appendChild(btn);
    var div = document.createElement("div");
    div.className = "exp-sec" + (isEvidence ? " evidence" : "");
    div.id = "sec-" + sec;
    div.innerHTML = innerHTML;
    holder.appendChild(div);
  }
  // wire one freshly-appended card's expanders / stepper / add button (used for added[] extras)
  function wireCardControls(scope) {
    var ex = scope.querySelectorAll(".linkbtn.ex");
    for (var i = 0; i < ex.length; i++) ex[i].addEventListener("click", function (e) { toggleSec(e.currentTarget.getAttribute("data-sec")); });
    var ab = scope.querySelectorAll(".addbtn[data-variant]");
    for (var j = 0; j < ab.length; j++) ab[j].addEventListener("click", addToCart);
    var qd = scope.querySelectorAll(".qd");
    for (var k = 0; k < qd.length; k++) qd[k].addEventListener("click", stepQty);
  }

  /* ----------------------------- Feature 3: human-trainer CTA + contact modal (§13.5) ----------------------------- */
  function ctaHTML(footer) {
    var txt = footer ? t().humanctafoot : t().humancta;
    return '<div class="human-cta-inner">' +
      '<svg class="hc-ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 19c0-3 2.6-5 5.5-5s5.5 2 5.5 5"/><circle cx="17.5" cy="9.5" r="2.4"/><path d="M15 14.6c2.4.2 4.5 1.9 4.5 4.4"/></svg>' +
      '<span class="hc-txt">' + esc(txt) + "</span>" +
      '<button type="button" class="hc-btn" data-act="contact">' + esc(t().humanbtn) + "</button></div>";
  }
  function renderHumanCta(hostId) {
    var host = $(hostId); if (!host) return;
    // C1 — on a SUCCESSFUL result list the escape hatch is a quiet footer ("Not quite what you wanted?"),
    // never a banner above results. The zero/can't-find host keeps its prominent class + failure framing.
    var footer = (hostId === "human-cta-results");
    if (footer) host.className = "human-cta footer";
    host.innerHTML = ctaHTML(footer);
    var b = host.querySelector(".hc-btn");
    if (b) b.addEventListener("click", openContact);
  }

  function openContact() {
    fireHumanCtaTelemetry();
    var scrim = $("rover-contact"); if (!scrim) return;
    // prefill the native contact message with the customer's query + parsed intent + vehicle.
    // refresh the prefill unless the customer has manually edited the body.
    var ta = $("rover-contact-message");
    if (ta && ta.getAttribute("data-edited") !== "1") {
      ta.value = (LASTPREFILL && LASTPREFILL.contact_message) || buildContactMessage();
      if (!ta._roverWired) {
        ta._roverWired = true;
        ta.addEventListener("input", function () { ta.setAttribute("data-edited", "1"); });
      }
    }
    scrim.hidden = false;
    scrim.classList.add("open");
    var cl = scrim.querySelector(".close"); if (cl) { try { cl.focus(); } catch (e) {} }
  }
  function closeContact() {
    var scrim = $("rover-contact"); if (!scrim) return;
    scrim.classList.remove("open");
    scrim.hidden = true;
  }
  // fallback message body if the server didn't ship capture_prefill.contact_message
  function buildContactMessage() {
    var q = LASTQ || currentQuery() || "";
    var veh = (LASTPREFILL && LASTPREFILL.vehicle_label) || vehLabel(VEH) || "";
    var parsed = (LASTPREFILL && LASTPREFILL.parsed_summary) || "";
    var lines = (LANG === "nl")
      ? ["Hoi,", "", "Ik zocht naar: " + q]
      : ["Hi,", "", "I was looking for: " + q];
    if (parsed) lines.push((LANG === "nl" ? "Wat ik bedoel: " : "What I mean: ") + parsed);
    if (veh) lines.push((LANG === "nl" ? "Mijn auto: " : "My vehicle: ") + veh);
    lines.push("");
    lines.push(LANG === "nl"
      ? "Kunnen jullie helpen het op te sporen?"
      : "Can you help track it down?");
    return lines.join("\n");
  }
  // §13.6 telemetry: log the human-CTA click. Fire the existing analytics hook if present, else no-op.
  // We do NOT invent an endpoint (the kickoff explicitly forbids POSTing this to /v1/capture).
  function fireHumanCtaTelemetry() {
    try {
      var detail = { query_id: lastQID, query: LASTQ, lang: LANG };
      document.dispatchEvent(new CustomEvent("rover:human_cta_click", { detail: detail, bubbles: true }));
      if (window.Rover && typeof window.Rover.onHumanCta === "function") window.Rover.onHumanCta(detail);
    } catch (e) { /* telemetry is best-effort */ }
  }

  /* ----------------------------- garage-lite session pill (§14) ----------------------------- */
  function pillLabelFor(key) {
    return (typeof MODEL_LABELS !== "undefined" && MODEL_LABELS[key]) || GL_FAMILY_LABELS[key] || String(key);
  }
  function adoptPill(key) {
    if (!GL_ENABLED || VEH || !key) return;
    if (PILL && PILL.key === key) return;                       // idempotent across narrowing turns
    var kind = GL_FAMILY_MODELS[key] && !(typeof MODEL_LABELS !== "undefined" && MODEL_LABELS[key])
      ? "family" : ((typeof MODEL_LABELS !== "undefined" && MODEL_LABELS[key]) ? "model" : null);
    if (!kind) return;                                          // unknown key — never guess
    PILL = { kind: kind, key: key, label: pillLabelFor(key) };
    saveLast(); renderVeh(); pillBeacon("pill_adopt");
  }
  // Free-typed model answers resolve SERVER-side; recover the key from interpretation.parsed.models.
  // Sound because the model question only ever fires when the query itself named no model — so
  // post-answer parsed.models is exactly the folded answer (1 key = model; full family set = family).
  function adoptPillFromResponse(data) {
    if (!GL_ENABLED || VEH) return;
    if (FACETS.model == null) return;                           // no model answer this round
    var key = String(FACETS.model);
    if ((typeof MODEL_LABELS !== "undefined" && MODEL_LABELS[key]) || GL_FAMILY_MODELS[key]) { adoptPill(key); return; }
    var m = (data.interpretation && data.interpretation.parsed && data.interpretation.parsed.models) || [];
    if (!m.length) return;                                      // unresolved free text — no pill, honest
    if (m.length === 1) { adoptPill(m[0]); return; }
    var set = {}; m.forEach(function (k) { set[k] = 1; });
    for (var fam in GL_FAMILY_MODELS) {
      var mem = GL_FAMILY_MODELS[fam];
      if (mem.length === m.length && mem.every(function (k) { return set[k] === 1; })) { adoptPill(fam); return; }
    }
  }
  function clearPill(rerun) {
    if (!PILL) return;
    PILL = null;
    // the pill ✕ also withdraws THIS round's model answer (mirror of the read-back chip ✕) —
    // otherwise the rerun still carries FACETS.model and adoptPillFromResponse re-adopts it
    // (found live 2026-07-03). The model question may legitimately come back; that's honest.
    if (FACETS.model != null) {
      delete FACETS.model;
      var di = NARROW.asked.indexOf("model"); if (di >= 0) NARROW.asked.splice(di, 1);
      delete NARROW.answers.model;
    }
    saveLast(); renderVeh(); pillBeacon("pill_clear");
    if (rerun && LASTQ) runSearch(LASTQ, true);
  }
  /* R2 conversion telemetry (restored 2026-07-03 — lost in the de-rebrand theme swap) + §14 pill
     lifecycle. ONE beacon path: allow-listed event_type + query_id (+ item_code + the AS-SHOWN fit
     verdict for conversion events). Best-effort through the App Proxy; never blocks or errors the
     storefront; presentation-only (verdict/ranking/safety untouched). */
  function roverBeacon(type, extra) {
    try {
      if (!lastQID) return;
      var b = { event_type: type, query_id: lastQID };
      if (extra) for (var k in extra) { if (extra[k] != null) b[k] = extra[k]; }
      var payload = JSON.stringify(b);
      if (navigator.sendBeacon) navigator.sendBeacon(api("/v1/event"), new Blob([payload], { type: "application/json" }));
      else postJSON("/v1/event", b).catch(function () {});
    } catch (e) { /* telemetry is best-effort */ }
  }
  function pillBeacon(type) { roverBeacon(type); }
  // fit AS SHOWN on the card (rail class) — presentation truth, exactly what the customer saw
  var RAIL_FIT = { green: "green", amber: "amber", grey: "grey", none: "not_listed" };
  function cardFit(card) {
    if (!card) return null;
    var m = (card.className || "").match(/rail-(green|amber|grey|none)/);
    return m ? RAIL_FIT[m[1]] : null;
  }
  function cardOf(el) {
    var card = el && el.closest ? el.closest(".card") : null;
    return { id: card ? ((card.id || "").replace(/^card-/, "") || null) : null, card: card };
  }

  var ANS_LABEL = { model: "Model", position: "Position", variant: "Variant", sub_type: "Type", quality: "Quality" };
  function renderChips(chips) {
    var host = $("parse-chips"); if (!host) return;
    var list = (chips || []).slice();
    // answered narrowing questions read back as removable chips — the customer can SEE and undo
    // every filter they've applied (visible proof the interview remembers their answers)
    Object.keys(FACETS).forEach(function (k) {
      if (list.some(function (c) { return (c.facet || "") === k; })) return;
      list.push({ label: ANS_LABEL[k] || humanizeKey(k), value: cleanChipLabel(String(FACETS[k]), k),
                  facet: k, removable: true });
    });
    // B4: a code query whose read-back has no chips -> synthesise a CODE chip so the box is never empty
    if (!list.length) {
      var code = parsedCode();
      if (code) list = [{ label: t().codechip, value: code, facet: "code", removable: false }];
    }
    host.innerHTML = list.map(function (c) {
      var x = c.removable ? '<button class="x" aria-label="remove ' + esc(humanizeVal(c.value)) + '" data-facet="' +
        esc(c.facet || "") + '">×</button>' : "";
      return '<span class="pchip"><span class="k">' + esc(humanizeKey(c.label)) + '</span><span class="v">' +
        esc(humanizeVal(c.value)) + "</span>" + x + "</span>";
    }).join("");

    // ---- signature read-back assembly: the paw nods in, then chips spring up in sequence ----
    var ico = root.querySelector(".readback .rb-ico");
    if (ico && !reduceMotion()) { ico.classList.remove("pop"); void ico.offsetWidth; ico.classList.add("pop"); }
    var pch = host.querySelectorAll(".pchip");
    for (var j = 0; j < pch.length; j++) {
      if (reduceMotion()) { pch[j].classList.add("in"); continue; }
      (function (el, idx) { setTimeout(function () { el.classList.add("in"); }, 120 + idx * 110); })(pch[j], j);
    }

    // removing a chip re-runs search with that facet cleared (contract §5)
    var btns = host.querySelectorAll(".x");
    for (var i = 0; i < btns.length; i++) btns[i].addEventListener("click", function (e) {
      var f = e.currentTarget.getAttribute("data-facet");
      e.currentTarget.closest(".pchip").remove();
      if (f && FACETS[f] != null) {
        // undo a narrowing ANSWER: clear the facet, un-ask the dimension, re-run as a narrowing
        // turn (the question may legitimately come back)
        delete FACETS[f];
        if (f === "model") clearPill(false);        // undoing the model answer = pill regret too
        var di = NARROW.asked.indexOf(f); if (di >= 0) NARROW.asked.splice(di, 1);
        delete NARROW.answers[f];
        runSearch(LASTQ || currentQuery(), true);
        return;
      }
      // soft client behaviour for parse chips: drop the chip and re-run the same query
      runSearch(currentQuery());
    });
  }

  function priceBlock(p) {
    if (!p || p.display == null) return "";
    // store is tax-exclusive — always show the ex-VAT note so the figure is never mistaken for incl. VAT
    return '<div class="price">' + esc(p.display) + '<span class="vat">' + t().vat + "</span></div>";
  }
  // stock as a unit count, mirroring the quick-search dropdown/strip: "34 in stock" / "No stock";
  // falls back to the state label when the qty is unknown. The dot colour still carries the state,
  // so a low-stock "2 in stock" reads amber — one number language across every surface.
  function stockQtyText(n, state) {
    if (n != null && n > 0) return n + (LANG === "nl" ? " op voorraad" : " in stock");
    if (n === 0 || state === "out_of_stock") return LANG === "nl" ? "Geen voorraad" : "No stock";
    return { in_stock: t().stock_in, low_stock: t().stock_low, out_of_stock: t().stock_out }[state] || "";
  }
  function stockBlock(s) {
    if (!s || !s.state) return "";
    var col = s.state === "out_of_stock" ? "#b3261e" : (s.state === "low_stock" ? "#9a6a12" : "#2f7a2f");
    var label = stockQtyText(s.qty, s.state) || s.label || "";
    return '<span class="q"><span class="qd" style="background:' + col + '"></span>' + esc(label) + "</span>";
  }

  var FIT_RANK = { green: 0, amber: 1, grey: 2, not_listed: 3 };
  function fitRank(f) { return FIT_RANK[f] != null ? FIT_RANK[f] : 2; }
  var CHEV = '<svg class="chev" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>';

  // strip ONE trailing letter that follows a digit (LR suffix variants: BH108061L -> BH108061),
  // after dropping non-alphanumerics — so near-miss code variants resolve to the same base.
  function baseCode(s) { return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/([0-9])[A-Z]$/, "$1"); }

  // alternate codes shown ON THE TILE FACE (subtle), under the primary code line. When the customer
  // typed an ALTERNATE (not the primary), its chip is highlighted — kills the "I searched X, saw Y"
  // confusion. Presentation only; alternative_codes already ships in every /v1/search result.
  function tileAltCodes(p) {
    var alts = (p.alternative_codes) || [];
    if (!alts.length) return "";
    var typed = parsedCode();                                   // normalised typed code, or ""
    var prim = String(p.code || "").toUpperCase().replace(/\s+/g, "");
    var primaryDiffers = !!typed && typed !== prim;             // they searched an alternate, not the primary
    var typedBase = baseCode(typed);
    var chips = alts.map(function (a) {
      var n = String(a).toUpperCase().replace(/\s+/g, "");
      var hit = primaryDiffers && (n === typed || (!!typedBase && baseCode(n) === typedBase));
      return '<span class="ac-tile' + (hit ? " matched" : "") + '"' +
        (hit ? ' title="' + esc(t().yoursearch) + '"' : "") + ">" + esc(a) +
        (hit ? '<span class="sr-only"> — ' + esc(t().yoursearch) + "</span>" : "") + "</span>";
    }).join("");
    return '<div class="tile-alts" aria-label="' + esc(t().alth) + '">' + chips + "</div>";
  }

  // a single result card. `grouped` = rendered inside a bracketed family group, where the product
  // name lives in the group header — so the member leads with its QUALITY (not a repeated title).
  function cardHTML(p, grouped) {
    var fc = fitClass(p.fit);
    var url = p.product_url || "";
    // ---- meta: code · quality · brand(OEM only) · stock  (brand suppressed for AM/Genuine, contract §6.5)
    // in a family group the quality becomes the heading, so it's dropped from the meta line.
    var meta = [];
    if (p.code) meta.push('<span class="code">' + esc(p.code) + "</span>");
    if (!grouped && p.quality) meta.push('<span class="qual">' + esc(p.quality) + "</span>");
    if (p.brand && String(p.quality).toUpperCase() === "OEM") meta.push('<span class="mfr">' + esc(p.brand) + "</span>");
    var stk = stockBlock(p.stock); if (stk) meta.push(stk);
    // ---- fit verdict (never icon-only) ----
    var fl = p.fit_label || (t().fitlabels[fc] || "");
    var fitLine = (p.fit ? '<span class="fit ' + fc + '" role="img" aria-label="' + esc(t().whyverdict) + ": " +
      esc(fl) + '">' + ICONS[fc] + esc(fl) + "</span>" : "");
    var critTag = p.safety_critical ?
      '<span class="crit-tag" title="' + esc(t().crittip) + '" aria-label="' + esc(t().crittip) +
      '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v6c0 4-3 6.5-7 9-4-2.5-7-5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg><span class="sr-only">' +
      esc(t().crit) + "</span></span>" : "";
    // ---- thumb + title link to the real product page (same tab; back returns to results) ----
    var thumbInner = (p.image && p.image.url) ?
      '<img src="' + esc(p.image.url) + '" alt="' + esc((p.image && p.image.alt) || p.title || "") + '" loading="lazy"' +
      ' onerror="this.onerror=null;if(this.parentNode)this.parentNode.innerHTML=window.__roverPh">' :
      PLACEHOLDER_SVG;
    var thumb = url
      ? '<a class="thumb" href="' + esc(url) + '" aria-label="' + esc(p.title || "") + '">' + thumbInner + "</a>"
      : '<div class="thumb" aria-hidden="true">' + thumbInner + "</div>";
    var heading = grouped ? (p.quality || p.title) : p.title;
    var hcls = "ptitle" + (grouped ? " qlead" : "");
    var title = url
      ? '<h3 class="' + hcls + '"><a href="' + esc(url) + '">' + esc(heading) + "</a></h3>"
      : '<h3 class="' + hcls + '">' + esc(heading) + "</h3>";

    // ---- expander sections (each toggled independently) ----
    var secs = [], toggles = [];
    function tog(sec, label) {
      toggles.push('<button class="linkbtn ex" aria-expanded="false" data-sec="' + sec + '">' + CHEV + esc(label) + "</button>");
    }
    // Fitment — always available (shows "paste VIN" when no model data), grounded in U_M_*/U_Tag_Model
    var fit = p.fitment || {};
    var fmodels = (fit.models || []).map(function (m) { return '<span class="fmodel">' + esc(m.label) + "</span>"; }).join("");
    var fitInner = "";
    if (fit.general) fitInner += '<div class="fit-all">' + esc(t().fitall) + "</div>";
    if (fmodels) fitInner += '<div class="fmodels">' + fmodels + "</div>";
    if (fit.notes) fitInner += '<div class="fnotes">' + esc(fit.notes) + "</div>";
    if (!fitInner) fitInner = '<div class="fnone">' + esc(t().fitnone) + "</div>";
    tog("fit-" + p.id, t().fitbtn);
    secs.push('<div class="exp-sec fitment" id="sec-fit-' + esc(p.id) + '"><h5>' + esc(t().fith) + "</h5>" + fitInner + "</div>");
    // FAQ — only if present (now populated by the service)
    var faq = ((p.faq) || []).map(function (q) { return "<dt>" + esc(q.q) + "</dt><dd>" + esc(q.a) + "</dd>"; }).join("");
    if (faq) { tog("faq-" + p.id, t().faqbtn); secs.push('<div class="exp-sec" id="sec-faq-' + esc(p.id) + '"><h5>' + esc(t().faqh) + '</h5><dl class="faq">' + faq + "</dl></div>"); }
    // Why this verdict — evidence
    if (p.fit) {
      var ev = ((p.evidence && p.evidence.lines) || []).map(function (e) {
        return '<div class="ev-row">' + EV_ICON.replace("{lvl}", e.level || "grey") + "<span>" + esc(e.text) + "</span></div>"; }).join("");
      var evFoot = (p.evidence && p.evidence.footer) || t().evfoot;
      tog("why-" + p.id, t().whyverdict);
      secs.push('<div class="exp-sec evidence" id="sec-why-' + esc(p.id) + '"><h5>' + esc(t().whyverdict) + "</h5>" + ev + '<div class="ev-foot">' + esc(evFoot) + "</div></div>");
    }
    // Details — description + "Also sold as"
    var alts = ((p.alternative_codes) || []).map(function (a) { return '<span class="ac">' + esc(a) + "</span>"; }).join("");
    var detInner = (p.description ? "<h5>" + esc(t().desch) + '</h5><div class="rdesc">' + descHtml(p.description) + "</div>" : "") +
      (alts ? "<h5>" + esc(t().alth) + '</h5><div class="altcodes">' + alts + "</div>" : "");
    if (detInner) { tog("det-" + p.id, t().detbtn); secs.push('<div class="exp-sec" id="sec-det-' + esc(p.id) + '">' + detInner + "</div>"); }

    // ---- actions: price + quantity stepper + add ----
    var qty = p.variant_id ?
      '<div class="qty" role="group" aria-label="' + esc(t().qtylab) + '"><button type="button" class="qd minus" aria-label="-" tabindex="-1">−</button>' +
      '<input class="qn" type="text" inputmode="numeric" value="1" aria-label="' + esc(t().qtylab) + '"><button type="button" class="qd plus" aria-label="+" tabindex="-1">+</button></div>' : "";
    var addBtn = p.variant_id ?
      '<button class="addbtn" data-variant="' + esc(p.variant_id) + '" data-url="' + esc(url) + '">' + esc(t().add) + "</button>" :
      (url ? '<a class="addbtn" href="' + esc(url) + '">' + esc(t().add) + "</a>" : "");

    return '<article class="card rail-' + fc + '" id="card-' + esc(p.id) + '">' +
      thumb +
      '<div class="info">' + title +
      '<div class="pmeta">' + meta.join("") + "</div>" +
      tileAltCodes(p) +
      '<div class="fitline">' + fitLine + critTag + "</div>" +
      '<div class="exp-row">' + toggles.join("") + "</div></div>" +
      '<div class="actions">' + priceBlock(p.price) + qty + addBtn + "</div>" +
      '<div class="expanders">' + secs.join("") + "</div></article>";
  }

  function renderCards(list, host) {
    if (!host) return;
    // group results that share a non-empty lr_number (U_WS_LRNo) — same part, different qualities.
    // Keep first-appearance order; only bracket when ≥2 members are present in this result set.
    var order = [], groups = {};
    (list || []).forEach(function (p) {
      var lr = (p.lr_number || "").trim();
      var key = lr ? "lr:" + lr : "solo:" + p.id;
      if (!groups[key]) { groups[key] = { lr: lr, items: [] }; order.push(key); }
      groups[key].items.push(p);
    });
    host.innerHTML = order.map(function (key) {
      var g = groups[key];
      if (g.lr && g.items.length >= 2) return famGroupHTML(g);
      return cardHTML(g.items[0]);
    }).join("");

    // wire expanders, quantity steppers, add-to-basket
    wireCardControls(host);
    if (host === $("cards") && CURRENT_SORT && CURRENT_SORT !== "fit") rvApplySort(CURRENT_SORT);

    // reveal: staggered rise (fresh paint) or a gentle in-place settle (re-grade). reduced-motion → no-op.
    if (!reduceMotion()) {
      var kids = host.children, idx = 0;
      for (var n = 0; n < kids.length; n++) {
        var el = kids[n];
        if (!el.classList || el.classList.contains("rover-showall") ||
            el.classList.contains("rover-more") || el.classList.contains("rover-tail-divider")) continue;
        if (CARD_REVEAL === "regrade") { el.classList.add("regrade"); }
        else { el.style.setProperty("--i", idx); el.classList.add("rv-in"); idx++; }
      }
    }
    overlayCardStock(list, host);
  }

  /* live stock on result cards — same /v1/stock batch + cache as the quick-search dropdown (via the
     RoverQS hook), so every surface shows ONE number: the shop's current inventory, not the snapshot's.
     Display-only: patches the meta line's stock chip; never re-ranks, never touches verdicts. */
  function overlayCardStock(list, host) {
    var qs = window.RoverQS;
    if (!qs || typeof qs.liveStock !== "function" || !host || !list || !list.length) return;
    var ids = [];
    list.forEach(function (p) { if (p && p.id) ids.push(p.id); });
    if (!ids.length) return;
    var COLOR = { out_of_stock: "#b3261e", low_stock: "#9a6a12", in_stock: "#2f7a2f" };
    qs.liveStock(ids, function (id, s) {
      if (!s || !COLOR[s.state]) return;
      var card = document.getElementById("card-" + id);
      if (!card || !host.contains(card)) return;
      var chip = card.querySelector(".pmeta .q");
      if (!chip) {
        var pm = card.querySelector(".pmeta");
        if (!pm) return;
        chip = document.createElement("span"); chip.className = "q";
        pm.appendChild(chip);
      }
      var label = stockQtyText(s.qty, s.state);
      chip.innerHTML = '<span class="qd" style="background:' + COLOR[s.state] + '"></span>' + esc(label);
    });
  }

  // one bracketed family group (shared part, different qualities). data-lr lets the pager merge later
  // pages of the same family into the existing group instead of opening a duplicate header.
  function famGroupHTML(g) {
    var members = g.items.slice().sort(function (a, b) {
      var d = fitRank(a.fit) - fitRank(b.fit); if (d) return d;
      return ((a.price && a.price.amount) || 1e12) - ((b.price && b.price.amount) || 1e12);
    });
    var gtitle = (members[0] && members[0].title) || "";
    return '<div class="fam-group" data-lr="' + esc(g.lr) + '">' +
      '<div class="fam-head"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h10"/></svg>' +
      '<span class="fam-title">' + esc(gtitle) + "</span>" +
      '<span class="fam-tag">' + esc(t().qualopts.replace("{n}", members.length)) + "</span>" +
      '<span class="fam-lr">' + esc(g.lr) + "</span></div>" +
      '<div class="fam-members" style="--fam-cols:' + Math.min(members.length, 3) + '">' +
      members.map(function (m) { return cardHTML(m, true); }).join("") + "</div></div>";
  }

  /* ----------------------------- result count + "Show more" pager (§13.7) ----------------------------- */
  function countCards(host) { return host ? host.querySelectorAll(".card").length : 0; }
  function setResCount(n) { var c = $("res-count"); if (c) c.textContent = n; }

  // Initialise the pager after a fresh render. initialRendered = how many of results[] are already on
  // screen (the full set for the happy path; a 6-card preview when a narrowing question is shown).
  function setupPager(data, host, initialRendered) {
    PAGER = {
      token: data.more_token || null,
      results: data.results || [],
      rendered: initialRendered,
      confidentTotal: (data.meta && data.meta.confident_total != null) ? data.meta.confident_total : (data.results || []).length,
      serverHasMore: !!(data.more_token && data.meta && data.meta.has_more),
      host: host, busy: false, dividerShown: false
    };
    renderPagerButton();
  }
  function pagerHasMore() { return (PAGER.rendered < PAGER.results.length) || PAGER.serverHasMore; }
  function renderPagerButton() {
    var host = PAGER.host; if (!host) return;
    var old = host.querySelector(".rover-more"); if (old) old.parentNode.removeChild(old);
    if (!pagerHasMore()) return;
    var b = document.createElement("button");
    b.type = "button"; b.className = "rover-more rover-showall";
    b.textContent = t().showmore;
    b.addEventListener("click", loadMore);
    host.appendChild(b);
  }
  function loadMore() {
    if (PAGER.busy || !pagerHasMore()) return;
    // phase A: reveal the confident remainder already in memory (no round-trip)
    if (PAGER.rendered < PAGER.results.length) {
      var next = PAGER.results.slice(PAGER.rendered, PAGER.rendered + 20);
      appendPageItems(next, PAGER.rendered);
      PAGER.rendered += next.length;
      renderPagerButton(); setResCount(countCards(PAGER.host));
      return;
    }
    // phase B: fetch the next page (confident overflow, then the labelled tail) from the service
    if (!PAGER.token) { PAGER.serverHasMore = false; renderPagerButton(); return; }
    PAGER.busy = true;
    var btn = PAGER.host.querySelector(".rover-more");
    if (btn) { btn.disabled = true; btn.classList.add("loading"); btn.textContent = t().showmoreing; }
    apiMore(PAGER.token, PAGER.rendered).then(function (res) {
      PAGER.busy = false;
      if (res && res.confident_total != null) PAGER.confidentTotal = res.confident_total;
      var items = (res && res.results) || [];
      appendPageItems(items, PAGER.rendered);
      PAGER.rendered = (res && res.offset != null) ? res.offset : (PAGER.rendered + items.length);
      PAGER.serverHasMore = !!(res && res.has_more) && items.length > 0;
      renderPagerButton(); setResCount(countCards(PAGER.host));
    }).catch(function () {
      PAGER.busy = false;
      var b2 = PAGER.host.querySelector(".rover-more");
      if (b2) { b2.disabled = false; b2.classList.remove("loading"); b2.textContent = t().showmore; }
    });
  }
  // append a page of products, dropping the "Other possible matches" divider where the confident set ends
  function appendPageItems(items, startIndex) {
    if (!items || !items.length || !PAGER.host) return;
    var host = PAGER.host, confPart = [], tailPart = [];
    items.forEach(function (p, i) {
      if ((startIndex + i) >= PAGER.confidentTotal) tailPart.push(p); else confPart.push(p);
    });
    if (confPart.length) appendGroupedPage(confPart, host);
    if (tailPart.length) {
      if (!PAGER.dividerShown) { insertTailDivider(host); PAGER.dividerShown = true; }
      appendGroupedPage(tailPart, host);
    }
    if (CURRENT_SORT && CURRENT_SORT !== "fit") rvApplySort(CURRENT_SORT);
  }
  function insertTailDivider(host) {
    var moreBtn = host.querySelector(".rover-more");
    var d = document.createElement("div");
    d.className = "rover-tail-divider";
    d.innerHTML = '<span>' + esc(t().tailhdr) + "</span>";
    if (moreBtn) host.insertBefore(d, moreBtn); else host.appendChild(d);
  }
  // append products with family grouping, merging into an existing family group when the lr_number matches
  function appendGroupedPage(items, host) {
    var order = [], groups = {};
    (items || []).forEach(function (p) {
      var lr = (p.lr_number || "").trim();
      var key = lr ? "lr:" + lr : "solo:" + p.id;
      if (!groups[key]) { groups[key] = { lr: lr, items: [] }; order.push(key); }
      groups[key].items.push(p);
    });
    var moreBtn = host.querySelector(".rover-more");
    order.forEach(function (key) {
      var g = groups[key];
      var existing = g.lr ? host.querySelector('.fam-group[data-lr="' + cssEsc(g.lr) + '"]') : null;
      if (existing) {
        var mem = existing.querySelector(".fam-members");
        g.items.forEach(function (m) {
          var node = htmlToNode(cardHTML(m, true));
          if (node) { mem.appendChild(node); wireCardControls(node); revealNode(node); }
        });
        var memCount = mem.querySelectorAll(".card").length;
        var tag = existing.querySelector(".fam-tag");
        if (tag) tag.textContent = t().qualopts.replace("{n}", memCount);
        mem.style.setProperty("--fam-cols", Math.min(memCount, 3));
      } else {
        var html = (g.lr && g.items.length >= 2) ? famGroupHTML(g) : cardHTML(g.items[0], false);
        var node2 = htmlToNode(html);
        if (node2) {
          if (moreBtn) host.insertBefore(node2, moreBtn); else host.appendChild(node2);
          wireCardControls(node2); revealNode(node2);
        }
      }
    });
    overlayCardStock(items, host);
  }
  function htmlToNode(html) { var t = document.createElement("div"); t.innerHTML = html; return t.firstChild; }
  function revealNode(node) { if (!reduceMotion() && node && node.classList) node.classList.add("regrade"); }

  // #5 — "Show all results": drop the narrowing question and show the full set + pager, so the customer
  // never reads the question's options as the limit of what we stock.
  function showAllResults(data, all) {
    hideNarrowPanel();
    var ch = $("cards"); if (ch) ch.classList.remove("ctx-faded");
    renderCards(all, ch);
    setResCount(countCards(ch));
    var cfor = $("res-count-for"); if (cfor) cfor.textContent = t().resultsfor;
    setupPager(data, ch, (all || []).length);
    renderHumanCta("human-cta-results");
  }

  function toggleSec(secid) {
    var el = $("sec-" + secid); if (!el) return;
    var open = el.classList.toggle("open");
    var btn = root.querySelector('.linkbtn.ex[data-sec="' + secid + '"]');
    if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
    if (open && String(secid).indexOf("why-") === 0) {
      var c = cardOf(el);
      if (c.id) roverBeacon("expand", { item_code: c.id, fit: cardFit(c.card) });
    }
  }

  function stepQty(e) {
    var btn = e.currentTarget, card = btn.closest(".card");
    var qn = card && card.querySelector(".qn"); if (!qn) return;
    var n = parseInt(qn.value, 10); if (!n || n < 1) n = 1;
    n += btn.classList.contains("plus") ? 1 : -1; if (n < 1) n = 1;
    qn.value = n;
  }
  function qtyOf(btn) {
    var card = btn.closest(".card"), qn = card && card.querySelector(".qn");
    var n = parseInt(qn && qn.value, 10); return (n && n > 0) ? n : 1;
  }

  function addToCart(e) {
    var btn = e.currentTarget, id = btn.getAttribute("data-variant"), url = btn.getAttribute("data-url");
    var qty = qtyOf(btn); btn.disabled = true;
    fetch("/cart/add.js", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: id, quantity: qty }), credentials: "same-origin"
    }).then(function (r) {
      if (!r.ok) throw new Error("cart " + r.status);
      return r.json();
    }).then(function () {
      var ci = cardOf(btn);
      if (ci.id) roverBeacon("add_to_cart", { item_code: ci.id, fit: cardFit(ci.card) });
      // "caught it" beat — the button springs, a paw appears, then settles back (D4)
      btn.classList.add("caught");
      btn.innerHTML = esc(t().addedok);
      refreshCart();                 // live-refresh the theme cart count + drawer, stay on results
      flash(t().addedok);
      setTimeout(function () { btn.classList.remove("caught"); btn.textContent = t().add; btn.disabled = false; }, 1800);
    }).catch(function () {
      btn.disabled = false;
      if (url) { btn.textContent = t().addfail; location.href = url; }
    });
  }

  // Refresh the host theme's cart UI after an ajax add — without a page reload. Pulls the live cart,
  // updates the common cart-count nodes, and fires the events the Empire/Shopify ajax-cart listens for.
  // (Exact Empire selector/event confirmed against the live theme at deploy.)
  function refreshCart() {
    fetch("/cart.js", { credentials: "same-origin" }).then(function (r) { return r.json(); }).then(function (cart) {
      var n = cart.item_count;
      // Empire renders the header count from the data-header-cart-count ATTRIBUTE via CSS ::after
      // (verified on the live theme), so set the attribute — never textContent — on attribute-driven
      // nodes (textContent would double the number); fall back to textContent for plain count nodes.
      var sels = ["[data-header-cart-count]", ".site-header-cart--count", "[data-cart-count]",
        ".cart-count", ".cart-count-bubble", ".site-header__cart-count", ".header__cart-count",
        "#CartCount", ".cart-link__bubble", ".js-cart-count"];
      sels.forEach(function (s) {
        var nodes = document.querySelectorAll(s);
        for (var i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          if (el.hasAttribute("data-header-cart-count")) el.setAttribute("data-header-cart-count", n);
          else if (el.hasAttribute("data-cart-count")) el.setAttribute("data-cart-count", n);
          else el.textContent = n;
          if (n > 0) el.classList.add("visible"); else el.classList.remove("visible");
        }
      });
      ["cart:refresh", "cart:updated", "cart:build", "ajaxProduct:added"].forEach(function (ev) {
        document.dispatchEvent(new CustomEvent(ev, { detail: { cart: cart }, bubbles: true }));
      });
      document.dispatchEvent(new CustomEvent("rover:added", { detail: { cart: cart } }));
    }).catch(function () { /* count stays as-is; add already succeeded */ });
  }

  /* ----------------------------- zero / empty / error ----------------------------- */
  function renderZero(data) {
    var cp = data.capture_prefill || {};
    var zt = $("zero-title"), zb = $("zero-body");
    if (zt) zt.textContent = t().zerotitle;
    if (zb) zb.textContent = t().zerobody;
    var q = $("cap-query"), v = $("cap-veh"), bg = $("zero-bestguess");
    if (q) q.value = cp.query_text || currentQuery() || "";
    if (v) v.value = cp.vehicle_label || vehLabel(VEH) || "";
    if (bg) bg.textContent = t().bestguess;
    $("capture-form").style.display = "";
    $("capture-done").classList.remove("show");
    renderHumanCta("human-cta-zero");          // §13.5 — prominent on the zero / can't-find state
    var zch = $("zero-cards"); if (zch) zch.classList.remove("ctx-faded");
    renderCards(data.results || [], $("zero-cards"));
  }
  function renderEmpty() {
    var zt = $("zero-title"), zb = $("zero-body");
    if (zt) zt.textContent = t().zerotitle;
    if (zb) zb.textContent = t().zerobody;
    renderZero({ results: [], capture_prefill: { query_text: currentQuery(), vehicle_label: vehLabel(VEH) } });
  }
  function paintError() {
    var et = $("err-title"), eb = $("err-body"), rb = $("err-retry");
    if (et) et.textContent = t().errtitle;
    if (eb) eb.textContent = t().errbody;
    if (rb) rb.textContent = t().retry;
  }
  function submitCapture() {
    var email = $("cap-email") ? $("cap-email").value : "";
    apiCapture({
      query_id: lastQID, email: email,
      query_text: $("cap-query") ? $("cap-query").value : currentQuery(),
      vehicle_label: $("cap-veh") ? $("cap-veh").value : vehLabel(VEH)
    }).then(function () {
      $("capture-form").style.display = "none";
      var d = $("capture-done"); d.querySelector("span").textContent = t().capdone; d.classList.add("show");
    }).catch(function () {
      $("capture-form").style.display = "none";
      var d = $("capture-done"); d.querySelector("span").textContent = t().capdone; d.classList.add("show");
    });
  }

  /* ----------------------------- garage / vehicle resolve ----------------------------- */
  function openGarage() { $("garage").classList.add("open"); var c = $("garage").querySelector(".close"); if (c) c.focus(); }
  function closeGarage() { $("garage").classList.remove("open"); }
  function setTab(name) {
    var tb = $("garage").querySelectorAll(".tabs button");
    for (var i = 0; i < tb.length; i++) tb[i].classList.toggle("on", tb[i].getAttribute("data-tab") === name);
    var pn = $("garage").querySelectorAll(".tabpane");
    for (var j = 0; j < pn.length; j++) pn[j].classList.toggle("on", pn[j].getAttribute("data-pane") === name);
  }

  function doResolve(method) {
    var payload = { method: method };
    if (method === "vin") payload.vin = val("g-vin");
    else if (method === "nl_plate") payload.plate = val("g-nl");
    else if (method === "uk_plate") payload.plate = val("g-uk");
    else if (method === "manual") {
      payload.model_key = val("g-man-model"); payload.engine = val("g-man-engine") || null;
      payload.year = parseInt(val("g-man-year"), 10) || null;
    }
    apiResolve(payload).then(function (res) { paintResolve(method, res); }).catch(function () {
      paintResolve(method, { vehicle: null, notice: "Lookup failed — try again or pick manually." });
    });
  }

  function paintResolve(method, res) {
    GARAGE.base = (res && res.vehicle) || null;
    GARAGE.confirm_models = (res && res.confirm_models) || [];
    GARAGE.picked = null;
    var box = $("rv-" + method) || $("rv-vin");
    // UK plate (or any route:'manual') bounces to the manual picker per Phase-4 design
    if ((res && res.route === "manual") || method === "uk_plate") {
      setTab("man");
      flash(t().ukmoved);
      return;
    }
    if (!res || (!res.vehicle && !(res.confirm_models && res.confirm_models.length))) {
      box.classList.add("show");
      box.querySelector(".rv").textContent = "";
      box.querySelector(".rd").textContent = (res && res.notice) || "Couldn’t resolve that — try the manual picker.";
      box.querySelector(".confirm-models").innerHTML = "";
      box.querySelector(".use-btn").style.display = "none";
      return;
    }
    box.classList.add("show");
    var v = res.vehicle;
    // confirm_models: model_key:null + candidates -> one-tap chooser (era-ambiguous VIN / undecodable VDS)
    if (res.confirm_models && res.confirm_models.length) {
      box.querySelector(".rv").textContent = (v && v.year ? v.year + " " : "") + "—";
      // don't repeat "confirm the generation" when the server notice already says it (#3)
      var gnotice = res.notice || "";
      box.querySelector(".rd").textContent = (gnotice + (/generation|generatie/i.test(gnotice) ? "" : " " + t().confirmgen)).trim();
      box.querySelector(".confirm-models").innerHTML = res.confirm_models.map(function (mk) {
        return '<button data-mk="' + esc(mk) + '">' + esc(MODEL_LABELS[mk] || mk) + "</button>";
      }).join("");
      var cb = box.querySelectorAll(".confirm-models button");
      for (var i = 0; i < cb.length; i++) cb[i].addEventListener("click", function (e) {
        var mk = e.currentTarget.getAttribute("data-mk");
        var sib = e.currentTarget.parentElement.querySelectorAll("button");
        for (var k = 0; k < sib.length; k++) sib[k].classList.remove("on");
        e.currentTarget.classList.add("on");
        GARAGE.picked = mk;
        box.querySelector(".rv").textContent = (v && v.year ? v.year + " " : "") + (MODEL_LABELS[mk] || mk);
      });
      box.querySelector(".use-btn").style.display = "";
    } else {
      box.querySelector(".rv").textContent = vehLabel(v);
      // precision drives the surface: model keeps safety caveats amber; variant unlocks amber->green/red
      box.querySelector(".rd").textContent = (v.precision === "variant") ? t().precvariant : t().precmodel;
      box.querySelector(".confirm-models").innerHTML = "";
      box.querySelector(".use-btn").style.display = "";
    }
  }

  function afterSave() {
    renderVeh();
    closeGarage();
    // nudge VIN ONLY when still model-level precision (once a VIN gives variant precision, no nudge — C2)
    if (VEH && VEH.precision !== "variant") flash(t().vinnudge);
    var onResults = $("screen-results") && $("screen-results").classList.contains("active");
    var onZero = $("screen-zero") && $("screen-zero").classList.contains("active");
    // C3 — re-grade the list already on screen in place (no "On the scent" replay, no scroll jump)
    if (LASTQ && onResults) regradeInPlace(LASTQ);
    else if (LASTQ && onZero) runSearch(LASTQ);
  }
  // in-place re-grade: re-query silently and repaint the shown results with a gentle settle, staying put
  function regradeInPlace(query) {
    if (!query || !query.trim()) return;
    LASTQ = query;
    ["q-results", "q-zero"].forEach(function (id) { var e = $(id); if (e) e.value = query; });
    apiSearch(query)
      .then(function (data) { renderResults(data, true); })
      .catch(function () { /* network hiccup — leave the current grading in place */ });
  }
  function useResolved() {
    // manual pane: no prior resolve call — resolve from the selects on demand, then save
    if (!GARAGE.base) {
      var model = val("g-man-model");
      if (!model) { flash("Pick a model to continue."); return; }
      apiResolve({ method: "manual", model_key: model, engine: val("g-man-engine") || null,
                   year: parseInt(val("g-man-year"), 10) || null })
        .then(function (res) { if (res && res.vehicle) { VEH = res.vehicle; afterSave(); } else { flash("Pick a model to continue."); } })
        .catch(function () { flash("Couldn’t save — try again."); });
      return;
    }
    var v = JSON.parse(JSON.stringify(GARAGE.base));
    if (GARAGE.confirm_models.length) {
      if (!GARAGE.picked) { flash(t().confirmgen); return; }
      v.model_key = GARAGE.picked;
      v.model_label = MODEL_LABELS[GARAGE.picked] || GARAGE.picked;
      // a confirmed VIN carries chassis -> exact variant fit on safety-critical
      v.precision = (v.chassis_sequence || v.vin) ? "variant" : "model";
    }
    VEH = v;
    afterSave();
  }

  function renderVeh() {
    var hosts = ["veh-row", "results-veh", "zero-veh"];
    var chip;
    if (VEH) {
      chip = '<span class="veh-chip">' + esc(vehLabel(VEH)) + '<button class="edit" data-act="garage">' + esc(t().change) + "</button></span>";
    } else if (GL_ENABLED && PILL) {
      // §14 session pill: "Browsing as: Defender ✕" — label opens the garage (upgrade path), ✕ clears
      chip = '<span class="veh-chip session">' + esc(t().glbrowse) + ": " + esc(PILL.label) +
        '<button class="edit" data-act="garage">' + esc(t().change) + "</button>" +
        '<button class="edit" data-act="pill-clear" aria-label="' + esc(t().glclear) +
        '" style="margin-left:4px">×</button></span>';
    } else {
      chip = '<button class="veh-add" data-act="garage">＋ ' + esc(t().addveh) + "</button>";
    }
    for (var i = 0; i < hosts.length; i++) { var el = $(hosts[i]); if (el) el.innerHTML = chip; }
    var fb = $("hero-fitbar"); if (fb) fb.style.display = VEH ? "none" : "flex";
    wireActs();
  }

  /* ----------------------------- wiring ----------------------------- */
  function val(id) { var e = $(id); return e ? e.value.trim() : ""; }
  function currentQuery() { var e = $("q-main") || $("q-results"); return e ? e.value : ""; }
  function flash(msg) {
    var f = $("rover-flash"); if (!f) return;
    f.textContent = msg; f.classList.add("show");
    clearTimeout(flash._t); flash._t = setTimeout(function () { f.classList.remove("show"); }, 4200);
  }
  function wireActs() {
    var a = root.querySelectorAll('[data-act="garage"]');
    for (var i = 0; i < a.length; i++) { a[i].onclick = openGarage; }
    var h = root.querySelectorAll('[data-act="home"]');
    for (var k = 0; k < h.length; k++) { h[k].onclick = resetToHero; }
    var c = root.querySelectorAll('[data-act="contact"]');
    for (var m = 0; m < c.length; m++) { c[m].onclick = openContact; }
    var pc = root.querySelectorAll('[data-act="pill-clear"]');
    for (var p2 = 0; p2 < pc.length; p2++) {
      pc[p2].onclick = function (e) { e.stopPropagation(); clearPill(true); };
    }
  }

  // backtrack: persist the last search (query + vehicle) per-tab so returning to the page
  // (browser Back from a product page, or a reload) restores the results instead of the hero.
  function saveLast() {
    try { sessionStorage.setItem("rover_last", JSON.stringify({ q: LASTQ, veh: VEH, pill: PILL })); } catch (e) {}
  }
  function restoreLast() {
    try {
      var s = JSON.parse(sessionStorage.getItem("rover_last") || "null");
      // Fast home: always start on the hero (no auto re-run). Keep the remembered vehicle.
      if (s && (s.veh || s.pill)) { VEH = s.veh || null; PILL = (GL_ENABLED && s.pill) || null; renderVeh(); }
    } catch (e) {}
  }

  // "Bring Rover home" — clear the query + vehicle and return to the hero (the home screen)
  function resetToHero() {
    VEH = null;
    PILL = null;
    GARAGE = { confirm_models: [], base: null, picked: null };
    LASTQ = "";
    resetNarrow();
    hideNarrowPanel();
    closeContact();
    var cards = $("cards"); if (cards) cards.classList.remove("ctx-faded");
    try { sessionStorage.removeItem("rover_last"); } catch (e) {}
    ["q-main", "q-results", "q-zero", "hero-vin", "hero-plate", "cap-query", "cap-veh", "cap-email"]
      .forEach(function (id) { var e = $(id); if (e) e.value = ""; });
    closeGarage();
    renderVeh();
    showState("hero");
    flash(t().homed);
    window.scrollTo(0, Math.max(0, root.offsetTop - 10));
    var qm = $("q-main"); if (qm) { try { qm.focus(); } catch (e) {} }
  }

  // model labels for the confirm_models chooser (mirrors contract §4 map)
  var MODEL_LABELS = {
    def_old: "Defender 1983–2006", def_new: "Defender 2007–2016", disco1: "Discovery 1", disco2: "Discovery 2",
    disco3: "Discovery 3", disco4: "Discovery 4", disco5: "Discovery 5", disco_sport: "Discovery Sport",
    rrc: "Range Rover Classic", rr_p38: "Range Rover P38", rr_l322: "Range Rover L322", rr_l405: "Range Rover L405",
    rr_l460: "Range Rover L460", rr_sport_l320: "RR Sport 2005–13", rr_sport_l494: "RR Sport 2014–22",
    rr_sport_l461: "RR Sport 2023+", evoque_1: "Evoque 2012–18", evoque_2: "Evoque 2019+", velar: "Velar",
    series1: "Series I", series_2_3: "Series II / III", freelander1: "Freelander 1", freelander2: "Freelander 2",
    jaguar: "Jaguar", general: "Fits all / general"
  };

  function bind() {
    // R2 click-through beacon — capture phase so it fires before navigation; sendBeacon survives unload
    document.addEventListener("click", function (e) {
      var a = e.target && e.target.closest ? e.target.closest(".card a.thumb, .card .ptitle a") : null;
      if (!a || !root.contains(a)) return;
      var c = cardOf(a);
      if (c.id) roverBeacon("click", { item_code: c.id, fit: cardFit(c.card) });
    }, true);
    // search forms
    var f1 = $("form-hero"), f2 = $("form-results");
    if (f1) f1.addEventListener("submit", function (e) { e.preventDefault(); runSearch(($("q-main") || {}).value); });
    if (f2) f2.addEventListener("submit", function (e) { e.preventDefault(); runSearch(($("q-results") || {}).value); });
    // suggested chips
    var sc = root.querySelectorAll("[data-seed]");
    for (var i = 0; i < sc.length; i++) sc[i].addEventListener("click", function (e) {
      var s = e.currentTarget.getAttribute("data-seed");
      if ($("q-main")) $("q-main").value = s; runSearch(s);
    });
    // garage controls
    var gt = root.querySelectorAll(".tabs button[data-tab]");
    for (var j = 0; j < gt.length; j++) gt[j].addEventListener("click", function (e) { setTab(e.currentTarget.getAttribute("data-tab")); });
    var lk = root.querySelectorAll("[data-resolve]");
    for (var k = 0; k < lk.length; k++) lk[k].addEventListener("click", function (e) { doResolve(e.currentTarget.getAttribute("data-resolve")); });
    var ub = root.querySelectorAll(".use-btn");
    for (var u = 0; u < ub.length; u++) ub[u].addEventListener("click", useResolved);
    var cl = $("garage").querySelector(".close"); if (cl) cl.addEventListener("click", closeGarage);
    $("garage").addEventListener("click", function (e) { if (e.target === $("garage")) closeGarage(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape" && $("garage").classList.contains("open")) closeGarage(); });
    // contact (human-trainer) modal — close button, backdrop click, Escape
    var cm = $("rover-contact");
    if (cm) {
      var cmClose = cm.querySelector(".close"); if (cmClose) cmClose.addEventListener("click", closeContact);
      cm.addEventListener("click", function (e) { if (e.target === cm) closeContact(); });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape" && cm.classList.contains("open")) closeContact(); });
      // re-open after a successful NATIVE /contact post (Shopify reloads the page) so the customer
      // sees the thank-you. Detect the server-rendered success/error block inside the modal.
      if (cm.querySelector(".contact-ok") || cm.querySelector(".contact-err")) {
        cm.hidden = false; cm.classList.add("open");
      }
    }
    // hero fitbar quick check + more-ways
    var hc = $("hero-check"); if (hc) hc.addEventListener("click", heroQuickCheck);
    var mw = $("hero-more"); if (mw) mw.addEventListener("click", openGarage);
    // capture + error retry
    var tb = $("cap-submit"); if (tb) tb.addEventListener("click", submitCapture);
    var rt = $("err-retry"); if (rt) rt.addEventListener("click", function () { runSearch(currentQuery()); });
    var sbsel = $("rv-sortby"); if (sbsel) sbsel.addEventListener("change", function () { CURRENT_SORT = sbsel.value; rvApplySort(CURRENT_SORT); });
    // puppy-story popover: CSS reveals on hover/focus; add a click toggle for touch + keep aria in sync
    var sw = root.querySelector(".story-wrap"), strig = root.querySelector(".story-trigger");
    if (sw && strig) {
      strig.addEventListener("click", function () {
        var open = sw.classList.toggle("open");
        strig.setAttribute("aria-expanded", open ? "true" : "false");
      });
      document.addEventListener("click", function (e) {
        if (!sw.contains(e.target)) { sw.classList.remove("open"); strig.setAttribute("aria-expanded", "false"); }
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && sw.classList.contains("open")) { sw.classList.remove("open"); strig.setAttribute("aria-expanded", "false"); strig.focus(); }
      });
    }
    wireActs();
  }

  function heroQuickCheck() {
    var vin = val("hero-vin"), plate = val("hero-plate");
    var country = (root.querySelector(".regwrap .cc button.on") || {}).getAttribute ?
      root.querySelector(".regwrap .cc button.on").getAttribute("data-cc") : "NL";
    if (vin) { openGarage(); setTab("vin"); if ($("g-vin")) $("g-vin").value = vin; doResolve("vin"); return; }
    if (plate) {
      openGarage();
      if (country === "UK") { setTab("uk"); if ($("g-uk")) $("g-uk").value = plate; doResolve("uk_plate"); }
      else { setTab("nl"); if ($("g-nl")) $("g-nl").value = plate; doResolve("nl_plate"); }
      return;
    }
    openGarage();
  }

  // public hooks (used by the section's inline language toggle, if present)
  window.Rover = {
    setLang: function (l) { LANG = (l === "nl" ? "nl" : "en"); document.documentElement.lang = LANG; renderVeh(); },
    search: runSearch, openGarage: openGarage
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bind);
  else bind();
  renderVeh();
  restoreLast();   // if returning to this page with a prior search, repaint the results
})();
/* rover-finder.js — Phase 5 + 2026-06-25 punch-list + 2026-06-26 card/cart/VAT tweaks
   + 2026-06-27 UX polish: signature read-back assembly, staggered/spring reveals, tactile press,
   add-to-basket "caught it", in-place re-grade, label-leak guard (B1/B2), CODE chip (B4),
   branded image fallback (B3), trainer CTA → footer (C1), stale VIN-nudge resolve (C2).
   + 2026-07-03 garage-lite session vehicle (§14): auto-adopt model answers as the per-tab
   "Browsing as" pill, session_vehicle wire, server-confirmed read-back, pill_adopt/pill_clear
   beacons, rover_last persistence, GL_ENABLED kill switch.
   + 2026-07-03 G2b: R2 conversion beacons RESTORED (click / why-verdict expand / add_to_cart,
   item_code + as-shown fit, delegated + unload-safe) — one roverBeacon path shared with the pill.
   + 2026-07-03 G2c: pill ✕ also withdraws the round's model answer (no re-adopt on rerun). */